/**
 * Dynamic Pricing Engine — Sheet Product JS
 *
 * Handles variant selection, price calculation, and cart submission
 * for the sheet (width x length) pricing model.
 *
 * Each product has width x length variants (up to 96 variants).
 * The variant price IS the total price for that W x L combo (cut fee already baked in).
 * Cart quantity = number of cuts ordered (NOT area).
 *
 * Grids are filtered per-product based on max_width and max_length metafields
 * passed via data attributes from Liquid.
 *
 * NO minimum price floor — Aaron explicitly said never to invent these.
 */

// How far past the variant grid we'll still PRICE a cut by rounding it up to
// the top bracket. The width grid often stops one odd-inch short of an even
// stock size (e.g. widths run ...43, 45, 47 but the sheet is 48") purely
// because Shopify caps a product at 250 variants — so a customer asking for
// the full advertised stock sheet would otherwise get bounced to a quote for
// a size we plainly sell off the shelf. Within this sliver we let the price
// round down to the top bracket (a ~2% break in the customer's favour, and a
// full-stock cut needs no rip anyway). Beyond it the area gap is real, so we
// keep routing to RFQ to preserve the round-down undercharge guard (order #3371).
var DPS_GRID_TOLERANCE_IN = 2;

// Customer-facing strings rendered by this JS at runtime. Injected from Liquid
// (locales/*.json) via window.MRU_DYNAMIC_PRICING_SHEET_I18N; every read carries
// the original EN literal as the fallback so the English storefront is unchanged
// even if the dict is absent. Interpolation token is {n} (and {size}/{max} where
// a string has two slots). See the | json injection block in
// snippets/dynamic-pricing-sheet.liquid.
var DPS_T = (typeof window !== 'undefined' && window.MRU_DYNAMIC_PRICING_SHEET_I18N) || {};
// A translation tool can register strings HTML-escaped (&quot; -> ") or leave a
// key missing (Shopify injects "Translation missing: ..."). Normalize DPS_T once
// so `DPS_T.key || 'fallback'` falls back on missing keys, and quotes + inline
// tags render instead of showing as literal &quot; / &lt;span&gt; text.
(function () {
  if (!DPS_T || typeof DPS_T !== 'object') return;
  function dec(s) {
    if (typeof s !== 'string' || s.indexOf('&') === -1) return s;
    return s.replace(/&lt;/g, '<').replace(/&gt;/g, '>')
            .replace(/&quot;/g, '"').replace(/&#0?34;/g, '"')
            .replace(/&#0?39;/g, "'").replace(/&#x27;/gi, "'")
            .replace(/&amp;/g, '&');
  }
  for (var k in DPS_T) {
    if (!Object.prototype.hasOwnProperty.call(DPS_T, k)) continue;
    var v = DPS_T[k];
    if (typeof v !== 'string') continue;
    if (v.indexOf('Translation missing') === 0 || v.indexOf('translation missing') === 0) { delete DPS_T[k]; continue; }
    DPS_T[k] = dec(v);
  }
})();

/* ============================================================================
   PANELS MODE (custom.pricing_model == "panels_v1"), SPEC sections 4, 8, 9.
   ----------------------------------------------------------------------------
   Everything in this block is PURE and node-testable (see the guarded
   module.exports at the bottom of the file, and
   scripts/tests/sheet-panel-pdp-harness.test.js).

   dpsAssignPanel is a hand port of assignPanel() in scripts/lib/sheet-brackets.js
   and must stay behaviourally identical: the PDP, the engine scripts and the SM3
   dashboard all have to name the same panel for the same cut, or a customer sees
   one price online and the counter quotes another. The harness pins both against
   sheet-bracket-pricing/TEST-VECTORS.json. If you change one, change all three.

   Products WITHOUT the flag never enter this block: they run the grid logic
   below, unchanged.
   ============================================================================ */
var DPS_PANELS_MODEL = 'panels_v1';

// Bumped when the assignment CONTRACT changes, mirroring ENGINE_VERSION in
// scripts/lib/sheet-brackets.js. The harness asserts the two agree, so a theme
// left behind on an old contract fails a test instead of mispricing a cart.
//   1 - initial panel model
//   2 - the 3" minimum cut (SPEC 4.0) and classifyCut()
var DPS_ENGINE_VERSION = 2;

// SPEC 4.0 (Aaron, 2026-08-12): under 3" the shear bends the metal instead of
// cutting it. That is not an RFQ (we cannot quote what we cannot make) and not a
// bill-up. Per-SKU overridable, carried on the ladder metafield as `min`.
var DPS_DEFAULT_MIN_CUT_IN = 3;

// The narrowest headroom strip the diagram will draw as hatching. Below it the
// hatch cannot show a full light-dark-light cycle (the gradient repeats every
// 8px, about 5.7px across it) and a two or three pixel band between the cut's
// 1px border and the panel's 1px border reads as a misaligned border rather than
// as metal on offer. Under the threshold renderPanelDiagram draws the panel as
// bought outright. See the note in renderPanelDiagram: only the drawing rounds.
var DPS_MIN_HATCH_PX = 10;

// The DISPLAY threshold for the quantity tier table, not a pricing threshold.
// FINAL-PLAN section 2.2: 0 in the engine, 0.05 on the storefront, because "a
// table headlining 1% is worse than no table". Below it the table is hidden and
// the line is still charged the tier rate, so this can never move a price.
var DPS_MIN_TIER_DISCOUNT = 0.05;

// The three outcomes of asking for a cut. Compare against THESE, never a typed
// string. Mirrors CUT_STATUS in the engine.
var DPS_CUT_STATUS = {
  OK: 'ok',
  BELOW_MIN_CUT: 'below-min-cut',
  OVERSIZE: 'oversize',
};

function dpsIsPosFinite(n) { return typeof n === 'number' && isFinite(n) && n > 0; }

function dpsNum(v) {
  var n = typeof v === 'number' ? v : parseFloat(v);
  return isFinite(n) ? n : NaN;
}

function dpsRoundTo(n, decimals) {
  var f = Math.pow(10, decimals);
  return Math.round(n * f) / f;
}

// Canonical rung string: `12`, `10.63`, `44.75`. Mirrors formatRung() in
// scripts/lib/sheet-brackets.js. Decimal rungs are real (10-5/8" copper stock
// resolves to a 10.62 rung), so nothing here may go through parseInt.
function dpsFormatRung(n) {
  var v = dpsNum(n);
  if (!isFinite(v)) return '';
  if (Number.isInteger(v)) return String(v);
  return String(dpsRoundTo(v, 2)).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
}

function dpsMakePanel(w, l) { return { w: w, l: l, area: dpsRoundTo(w * l, 6) }; }

/**
 * Hand port of the engine's parseOptionDimension. Panel option values carry a
 * text prefix ("Up to 8.5" width"); grid values do not ("3" width"). This reads
 * both, keeps decimals, and returns NaN when no number is present.
 */
function dpsParseOptionDim(value) {
  var m = String(value == null ? '' : value).match(/(\d+(?:\.\d+)?)/);
  return m ? parseFloat(m[1]) : NaN;
}

/**
 * French size values back to the English form every reader in this file
 * expects (2026-09-22). On /fr the variant options are translated
 * ("largeur jusqu'à 11,75 po", "longueur 12 po"), and parseInt on them is NaN,
 * so the FR grid configurator never priced (same bug dynamic-pricing.js fixed
 * 2026-09-07). English values never match, so English is untouched.
 */
function dpsOptionToEn(value) {
  var s = String(value == null ? '' : value);
  var m = s.match(/^\s*(largeur|longueur)\s+(jusqu['\u2019]\u00e0\s+)?([0-9][0-9\s\u00a0\u202f]*(?:,[0-9]+)?(?:-[0-9]+\/[0-9]+)?|[0-9]+\/[0-9]+)\s*po\s*$/i);
  if (!m) return s;
  var num = m[3].replace(/[\s\u00a0\u202f]/g, '').replace(',', '.');
  return (m[2] ? 'Up to ' : '') + num + '" ' + (m[1].toLowerCase() === 'largeur' ? 'width' : 'length');
}

// Canonical rung ordering: area, then perimeter, then width. Total + stable.
function dpsComparePanels(a, b) {
  if (a.area !== b.area) return a.area - b.area;
  var pa = a.w + a.l, pb = b.w + b.l;
  if (pa !== pb) return pa - pb;
  if (a.w !== b.w) return a.w - b.w;
  return a.l - b.l;
}

function dpsFits(panel, w, l) { return panel.w >= w && panel.l >= l; }

function dpsCutResult(status, extra) {
  var out = {
    status: status,
    ok: status === DPS_CUT_STATUS.OK,
    belowMinCut: status === DPS_CUT_STATUS.BELOW_MIN_CUT,
    oversize: status === DPS_CUT_STATUS.OVERSIZE,
    panel: null,
    swapFits: false,
  };
  for (var k in extra) {
    if (Object.prototype.hasOwnProperty.call(extra, k)) out[k] = extra[k];
  }
  return out;
}

/**
 * Classify a requested cut: makeable, too small to shear, or bigger than stock.
 * Hand port of classifyCut() in scripts/lib/sheet-brackets.js (SPEC 4).
 *
 *   0. Either dimension under minCutIn -> BELOW_MIN_CUT. Checked FIRST, before
 *      rotation, because the minimum is per dimension: a 2 x 50 is uncuttable
 *      whichever way you turn it, and a directional 50 x 2 therefore gets NO
 *      swap prompt (offering one would be a lie).
 *   1. Rotation allowed -> normalize so cw <= cl.
 *   2-3. Smallest containing rung by area, then perimeter, then width.
 *   4. No candidate -> OVERSIZE (the RFQ path).
 *   5. Rotation-locked miss that would fit swapped -> OVERSIZE carrying swapFits.
 *
 * ALWAYS returns an object, never null, never throws. Garbage classifies as
 * BELOW_MIN_CUT with `invalid: true`.
 */
function dpsClassifyCut(ladder, cut, opts) {
  var options = opts || {};
  var minCutIn = dpsIsPosFinite(dpsNum(options.minCutIn))
    ? dpsNum(options.minCutIn)
    : (ladder && dpsIsPosFinite(dpsNum(ladder.minCutIn)) ? dpsNum(ladder.minCutIn) : DPS_DEFAULT_MIN_CUT_IN);

  var cw = cut ? dpsNum(cut.w) : NaN;
  var cl = cut ? dpsNum(cut.l) : NaN;
  var base = { minCutIn: minCutIn, cut: { w: cw, l: cl } };

  if (!dpsIsPosFinite(cw) || !dpsIsPosFinite(cl)) {
    return dpsCutResult(DPS_CUT_STATUS.BELOW_MIN_CUT,
      { minCutIn: minCutIn, cut: base.cut, invalid: true, offending: ['w', 'l'] });
  }

  // Rule 0. Before rotation, before candidates, before anything else.
  if (cw < minCutIn || cl < minCutIn) {
    var offending = [];
    if (cw < minCutIn) offending.push('w');
    if (cl < minCutIn) offending.push('l');
    return dpsCutResult(DPS_CUT_STATUS.BELOW_MIN_CUT,
      { minCutIn: minCutIn, cut: base.cut, offending: offending });
  }

  if (!ladder || !ladder.rungs || !ladder.rungs.length) {
    return dpsCutResult(DPS_CUT_STATUS.OVERSIZE, base);
  }

  var rotationAllowed = options.rotationAllowed != null
    ? !!options.rotationAllowed
    : ladder.rotation !== false;

  // Rotation on: a cut and its swap are the same physical part, so normalize.
  var aw = rotationAllowed ? Math.min(cw, cl) : cw;
  var al = rotationAllowed ? Math.max(cw, cl) : cl;

  var best = null, i, panel, hit;
  for (i = 0; i < ladder.rungs.length; i++) {
    panel = ladder.rungs[i];
    hit = false;
    if (dpsFits(panel, aw, al)) hit = true;
    else if (rotationAllowed && dpsFits(panel, al, aw)) hit = true;
    if (!hit) continue;
    if (!best || dpsComparePanels(panel, best) < 0) best = panel;
  }

  if (best) {
    return dpsCutResult(DPS_CUT_STATUS.OK, {
      minCutIn: minCutIn,
      cut: base.cut,
      panel: { w: best.w, l: best.l, area: best.area },
      rotated: rotationAllowed && cw > cl,
      exact: best.w === aw && best.l === al,
    });
  }

  // Nothing fits as entered. On a rotation-locked SKU, check the swapped cut:
  // order history says customers type "48 x 16" on 22.75 x 48 perforated stock
  // and mean 16 across the pattern. One tap accepts, declining goes to RFQ.
  if (!rotationAllowed) {
    var swapBest = null;
    for (i = 0; i < ladder.rungs.length; i++) {
      panel = ladder.rungs[i];
      if (!dpsFits(panel, cl, cw)) continue;
      if (!swapBest || dpsComparePanels(panel, swapBest) < 0) swapBest = panel;
    }
    if (swapBest) {
      return dpsCutResult(DPS_CUT_STATUS.OVERSIZE, {
        minCutIn: minCutIn,
        cut: base.cut,
        swapFits: true,
        swap: {
          cut: { w: cl, l: cw },
          panel: { w: swapBest.w, l: swapBest.l, area: swapBest.area },
        },
      });
    }
  }

  return dpsCutResult(DPS_CUT_STATUS.OVERSIZE, base);
}

/**
 * Shape-compatible wrapper over dpsClassifyCut, matching engine assignPanel().
 * PREFER dpsClassifyCut in new code: it is the only one that can say WHY a cut
 * failed, and "too small to shear" versus "too big for stock" is the difference
 * between an apology and a quote form.
 *
 *   hit        -> { panel:{w,l,area}, rotated, exact, swapFits:false, cut:{w,l} }
 *   swap only  -> { panel:null, swapFits:true, swap:{cut:{w,l}, panel:{...}}, cut:{w,l} }
 *   no panel   -> null   (oversize OR below the minimum cut)
 *
 * ENGINE v2 BEHAVIOUR CHANGE: a sub-3" cut returns null here, where v1 billed it
 * up to the smallest panel.
 */
function dpsAssignPanel(ladder, cut, opts) {
  var res = dpsClassifyCut(ladder, cut, opts);
  if (res.ok) {
    return {
      panel: res.panel,
      rotated: res.rotated,
      exact: res.exact,
      swapFits: false,
      cut: res.cut,
    };
  }
  if (res.oversize && res.swapFits) {
    return { panel: null, swapFits: true, swap: res.swap, cut: res.cut };
  }
  return null;
}

function dpsPanelOf(assignment) {
  return assignment && assignment.panel ? assignment.panel : null;
}

/**
 * Rebuild a ladder from the compact custom.panel_ladder metafield
 * ({ v, rot, tier, sw, sl, p:[[w,l],...] }). Mirrors ladderFromMetafieldValue()
 * in scripts/lib/sheet-brackets.js. Returns null when the value is unusable, and
 * the caller then falls back to grid mode.
 */
function dpsLadderFromMetafieldValue(value) {
  var v = value;
  if (typeof v === 'string') {
    try { v = JSON.parse(v); } catch (err) { return null; }
  }
  // Rungs are read verbatim. The PDP never derives a rung from a stock
  // dimension, so the engine's stock-axis resolution (which floors 10.63 to the
  // real 10.625 sheet, see resolveStockAxis in scripts/lib/sheet-brackets.js)
  // arrives here already applied and cannot be re-broken by the theme.
  if (!v || !Array.isArray(v.p)) return null;
  var rungs = [];
  for (var i = 0; i < v.p.length; i++) {
    var pair = v.p[i];
    if (!pair || pair.length < 2) continue;
    var w = dpsNum(pair[0]);
    var l = dpsNum(pair[1]);
    if (!dpsIsPosFinite(w) || !dpsIsPosFinite(l)) continue;
    rungs.push(dpsMakePanel(w, l));
  }
  if (!rungs.length) return null;
  rungs.sort(dpsComparePanels);
  return {
    rungs: rungs,
    rotation: v.rot !== 0,
    tier: v.tier || null,
    stockMaxWidth: v.sw != null ? dpsNum(v.sw) : null,
    stockMaxLength: v.sl != null ? dpsNum(v.sl) : null,
    // v1 payloads have no `min`; fall back to the config default rather than to
    // zero, or a stale metafield would quietly disable the minimum cut.
    minCutIn: dpsIsPosFinite(dpsNum(v.min)) ? dpsNum(v.min) : DPS_DEFAULT_MIN_CUT_IN,
  };
}

// `12x12`, `10.62x90`. The variant index key in panels mode.
function dpsPanelKey(w, l) { return dpsFormatRung(w) + 'x' + dpsFormatRung(l); }

function dpsFloorCents(n) { return Math.floor(n * 100 + 1e-9) / 100; }

/* ── THE QUANTITY TABLE ──────────────────────────────────────────────────────
   A hand port of chargeFromQuantityTable() in scripts/lib/sheet-brackets.js,
   pinned against it by the harness on every quantity vector. Same reason
   dpsAssignPanel is a hand port: a theme asset cannot require() the library, and
   the storefront, the cart and the counter have to name the same number.

   `custom.pricing_config.nest` carries a rate per LADDER RUNG:

       { v:1, p: [[w, l, [[qty, basisPoints], ...]], ...] }

   and the charge is

       perPiece = floorCents(variantPrice x (1 - bp / 10000))
       line     = perPiece x quantity

   PER PIECE, THEN MULTIPLIED. Never a percentage on the line total: a line
   rounds once and a split line rounds twice, so the percentage form lets 2 + 8
   beat 10 by a cent. The Shopify Function runs this same arithmetic off this
   same stored rate, which is what makes the number on this page the number in
   the cart rather than a number that merely agrees today.

   NEVER SUM OR INTERPOLATE ROWS. The rows are single-block prices at published
   quantities; the deepest row at or below the quantity sets the rate and keeps
   applying above the largest published one. Adding rows up, or interpolating
   between them, quotes HIGH (seven 5x6 are $7.02 each against the 4-row's
   $7.19) and is the defect this comment exists to prevent.

   Fails OPEN: a missing, malformed or unknown-panel table returns full price
   with basisPoints 0, never a throw and never a zero.
   ========================================================================== */
function dpsChargeFromQuantityTable(value, panel, variantPrice, quantity) {
  var price = dpsNum(variantPrice);
  var qty = Math.round(dpsNum(quantity));
  var out = {
    basisPoints: 0,
    perPiece: price,
    lineTotal: dpsRoundTo(price * (qty || 0), 2),
    quantity: qty,
    tier: null,
    tiers: [],
  };
  if (!dpsIsPosFinite(price) || !dpsIsPosFinite(qty)) return out;

  var v = value;
  if (typeof v === 'string') {
    try { v = JSON.parse(v); } catch (e) { return out; }
  }
  if (!v || !v.p || !v.p.length || !panel) return out;

  var w = dpsNum(panel.w);
  var l = dpsNum(panel.l);
  var row = null;
  for (var i = 0; i < v.p.length; i++) {
    var r = v.p[i];
    if (!r || r.length < 3) continue;
    if (dpsNum(r[0]) === w && dpsNum(r[1]) === l) { row = r[2]; break; }
  }
  if (!row || !row.length) return out;

  var tiers = [];
  for (var j = 0; j < row.length; j++) {
    var t = row[j];
    if (!t || t.length < 2) continue;
    var q = Math.round(dpsNum(t[0]));
    var bp = Math.round(dpsNum(t[1]));
    if (!dpsIsPosFinite(q) || !isFinite(bp) || bp <= 0 || bp >= 10000) continue;
    tiers.push({ quantity: q, basisPoints: bp, perPiece: dpsFloorCents(price * (1 - bp / 10000)) });
  }
  tiers.sort(function (a, b) { return a.quantity - b.quantity; });
  out.tiers = tiers;

  var active = null;
  for (var k = 0; k < tiers.length; k++) if (tiers[k].quantity <= qty) active = tiers[k];
  if (!active) return out;

  out.tier = active;
  out.basisPoints = active.basisPoints;
  out.perPiece = active.perPiece;
  out.lineTotal = dpsRoundTo(active.perPiece * qty, 2);
  return out;
}

// Panel copy. Inline EN/FR branching keyed off the document language, the same
// pattern the cart uses (assets/cart-readiness.js). Deliberately NOT locale
// files: SPEC section 9 owns these strings.
// ONE size separator everywhere, U+00D7, prose and chrome alike (SPEC section 9,
// revised 2026-08-12). See sizeLabel().
var DPS_PANEL_STR = {
  en: {
    note: function (cut, panel) { return 'Your ' + cut + ' is cut from our ' + panel + ' panel. Same price up to ' + panel + '.'; },
    noteFull: function (panel) { return 'You are taking the full ' + panel + ' panel.'; },
    upsell: function (panel) { return 'Take the full ' + panel + ' instead. Costs nothing extra.'; },
    // Diagram (the picker is gone, so the drawing carries the whole job).
    payFor: 'What you pay for',
    yourCutTag: 'Your cut',
    headroom: 'Same price up to here',
    smallestTag: 'The smallest we can cut',
    stockTag: 'Our sheet',
    minLabel: function (min) { return 'Minimum ' + min; },
    summary: function (n, cut, panel) { return n + ' x ' + cut + ', cut from a ' + panel + ' panel'; },
    summaryFull: function (n, panel) { return n + ' x ' + panel + ' panel'; },
    summaryNone: function (n, cut) { return n + ' x ' + cut + ', no standard panel covers this'; },
    swapTitle: 'Check the direction',
    swapBody: function (asEntered, swapW, swapL) {
      return 'This one has a pattern that runs one way, so ' + asEntered + " doesn't fit the sheet as you entered it. Flipped it does: " + swapW + ' across the pattern by ' + swapL + ' along it.';
    },
    swapYes: function (size) { return 'Use ' + size; },
    swapNo: 'Request a quote instead',
    // SPEC 4.0 / section 9. Not an RFQ: we cannot quote what the shear cannot make.
    minCut: function (min) { return min + ' minimum per side. Any smaller and the shear bends the metal.'; },
    minCutAtc: function (min) { return 'Minimum ' + min + ' per side'; },
    summaryBelowMin: function (n, cut, min) { return n + ' x ' + cut + ', under the ' + min + ' minimum'; },
    // Quantity tiers. Block language only, no mention of cuts or fees: the live
    // FAQ still says cuts are free whether you order one piece or forty, and a
    // saving whose story is "we charge you less labour" contradicts it
    // (FINAL-PLAN section 6). "More come out of one block" does not.
    qtyEyebrow: 'Taking more than one',
    qtyNote: 'Your panels come out of one block, so the more you take, the less each one costs.',
    qtyPieces: function (n) { return n + ' pieces'; },
    qtyEach: function (money) { return money + ' each'; },
    qtySave: function (pct) { return 'save ' + pct + '%'; },
    qtyYoureHere: 'your quantity',
    // The compact ask (Aaron's rev-3 direction, 2026-08-14). The price belongs
    // to THEIR cut; the bracket is one muted line; quantity is part of the ask.
    heroForCut: function (cut) { return 'for your ' + cut; },
    bracketLine: function (panel) { return 'Same price for any cut up to ' + panel + '.'; },
    heroHint: function (n, cut) { return 'Press Get price to quote ' + n + ' × ' + cut + '.'; },
    perPieceLbl: function (cut) { return 'Per piece, ' + cut; },
    discountLbl: function (n) { return 'Volume discount, ' + n + ' pieces'; },
  },
  fr: {
    note: function (cut, panel) { return 'Votre ' + cut + ' est coupé dans notre panneau standard de ' + panel + ". Même prix jusqu'à " + panel + '.'; },
    noteFull: function (panel) { return 'Vous prenez le panneau complet de ' + panel + '.'; },
    upsell: function (panel) { return 'Prenez le panneau complet de ' + panel + '. Ça ne coûte pas un sou de plus.'; },
    payFor: 'Ce que vous payez',
    yourCutTag: 'Votre coupe',
    headroom: "Même prix jusqu'ici",
    smallestTag: "La plus petite coupe qu'on peut faire",
    stockTag: 'Notre feuille',
    minLabel: function (min) { return 'Minimum ' + min; },
    summary: function (n, cut, panel) { return n + ' x ' + cut + ', coupé dans un panneau de ' + panel; },
    summaryFull: function (n, panel) { return n + ' x panneau de ' + panel; },
    summaryNone: function (n, cut) { return n + ' x ' + cut + ', aucun panneau standard ne couvre cette dimension'; },
    swapTitle: 'Vérifiez le sens',
    swapBody: function (asEntered, swapW, swapL) {
      return 'Cette feuille a un motif qui court dans un sens, donc ' + asEntered + " n'y entre pas comme vous l'avez entré. Inversé, ça passe : " + swapW + ' en travers du motif par ' + swapL + ' dans le sens du motif.';
    },
    swapYes: function (size) { return 'Utiliser ' + size; },
    swapNo: 'Demander une soumission',
    minCut: function (min) { return 'Minimum ' + min + ' par côté. Plus petit que ça, la cisaille plie le métal.'; },
    minCutAtc: function (min) { return 'Minimum ' + min + ' par côté'; },
    summaryBelowMin: function (n, cut, min) { return n + ' x ' + cut + ', sous le minimum de ' + min; },
    qtyEyebrow: 'Vous en prenez plusieurs',
    qtyNote: 'Vos panneaux sortent tous du même bloc, donc plus vous en prenez, moins chacun vous coûte.',
    qtyPieces: function (n) { return n + ' pièces'; },
    qtyEach: function (money) { return money + ' chacun'; },
    // Espace avant le %, comme le veut la typographie française.
    qtySave: function (pct) { return 'économisez ' + pct + ' %'; },
    qtyYoureHere: 'votre quantité',
    heroForCut: function (cut) { return 'pour votre ' + cut; },
    bracketLine: function (panel) { return "Même prix pour toute coupe jusqu'à " + panel + '.'; },
    heroHint: function (n, cut) { return 'Appuyez sur Obtenir le prix pour ' + n + ' × ' + cut + '.'; },
    perPieceLbl: function (cut) { return 'Par pièce, ' + cut; },
    discountLbl: function (n) { return 'Rabais de volume, ' + n + ' pièces'; },
  },
};

if (typeof customElements !== 'undefined' && !customElements.get('dynamic-pricing-sheet')) {
  class DynamicPricingSheet extends HTMLElement {
    constructor() {
      super();
      // Grids derived from actual variant data in connectedCallback
      this.widthGrid = [];
      this.lengthGrid = [];
      this.standardSizes = [];
      this.maxWidth = 96;
      this.maxLength = 96;
      this.defaultWidth = 12;
      this.defaultLength = 12;
      this.currentWidth = this.defaultWidth;
      this.currentLength = this.defaultLength;
      this.currentQty = 1;
      this.isCustom = false;
      this.oversizeInfo = null; // set when entered W/L exceeds stock max → routes to RFQ
      this.snapInfo = null; // set when a typed W/L was rounded to the nearest 1/8" → shows the snap note
      this.variants = [];
      this.variantIndex = {}; // O(1) lookup map: "WxL" -> variant
      this.isFr = (typeof document !== 'undefined' && document.documentElement && /^fr/i.test(document.documentElement.lang || ''));
      this.productUrl = '';
      this.pricingConfig = null;
      // ── panels mode (custom.pricing_model == "panels_v1") ──
      this.panelsMode = false;
      this.ladder = null;        // { rungs, rotation, tier, stockMaxWidth, stockMaxLength }
      this.panelIndex = {};      // "WxL" (dpsPanelKey) -> variant
      this.swapInfo = null;      // set when the cut only fits with its axes swapped
      this.swapDeclined = false; // the customer said no to the swap; show RFQ instead
      this.belowMinInfo = null;  // set when a dimension is under the minimum cut (SPEC 4.0)
      // The per-rung quantity table from custom.pricing_config.nest. Null until
      // the daily sync emits one, and null is the whole rollback: no table means
      // no tier UI and a line total of price x quantity, exactly as today.
      this.nestTable = null;
      this.qtyTiersEl = null;
      // ── THE GET PRICE GATE (Aaron, 2026-08-14) ──
      // The quoted ask: size AND quantity. Money surfaces render from THIS,
      // never from live typing; an edit to any of the three inputs re-opens the
      // ask (dps-is-pending) until Get price commits it. Seeded with the land
      // default in connectedCallback: land is ALWAYS priced (the SEO contract -
      // crawlers and the product feed read the default panel's price).
      this.quotedW = null;
      this.quotedL = null;
      this.quotedQty = null;
    }

    connectedCallback() {
      // Parse variant data from the embedded JSON
      var dataEl = this.querySelector('[data-dps-variants]');
      if (dataEl) {
        try {
          this.variants = JSON.parse(dataEl.textContent);
          for (var vn = 0; vn < this.variants.length; vn++) {
            this.variants[vn].option1 = dpsOptionToEn(this.variants[vn].option1);
            this.variants[vn].option2 = dpsOptionToEn(this.variants[vn].option2);
          }
        } catch (e) {
          console.error('Dynamic Pricing Sheet: Could not parse variant data', e);
          return;
        }
      }

      // Parse pricing_config for volume/sale data
      var configEl = this.querySelector('[data-dps-pricing-config]');
      if (configEl) {
        try { this.pricingConfig = JSON.parse(configEl.textContent); } catch (e) { /* ignore */ }
      }

      // Panels mode gate. The ladder is injected from custom.panel_ladder by
      // Liquid; the PDP never reconstructs it from variants (SPEC 3.3). A missing
      // or unusable ladder falls back to the grid path below, so a half-flipped
      // product degrades to today's behavior instead of showing a dead panel.
      if ((this.dataset.pricingModel || '') === DPS_PANELS_MODEL) {
        var ladderEl = this.querySelector('[data-dps-panel-ladder]');
        if (ladderEl) {
          try {
            this.ladder = dpsLadderFromMetafieldValue(JSON.parse(ladderEl.textContent));
          } catch (e) {
            this.ladder = null;
          }
        }
        if (this.ladder && this.ladder.rungs.length) {
          this.panelsMode = true;
        } else {
          console.warn('Dynamic Pricing Sheet: pricing_model is panels_v1 but the panel ladder is missing or unusable; falling back to grid pricing');
        }
        // Panels only. A grid product's pricing_config has no `nest` block and
        // must never grow one: the legacy volume ladder is its quantity story.
        if (this.panelsMode && this.pricingConfig && this.pricingConfig.nest
            && this.pricingConfig.nest.p && this.pricingConfig.nest.p.length) {
          this.nestTable = this.pricingConfig.nest;
        }
      }

      // Weekly sale banner countdown (Liquid renders the banner when
      // pricing_config.saleAll is set; this just runs the clock).
      this.initWeeklyBanner();

      // Build O(1) variant index: "WxL" -> variant
      for (var idx = 0; idx < this.variants.length; idx++) {
        var vw = parseInt(String(this.variants[idx].option1), 10);
        var vl = parseInt(String(this.variants[idx].option2), 10);
        if (!isNaN(vw) && !isNaN(vl)) {
          this.variantIndex[vw + 'x' + vl] = this.variants[idx];
        }
      }

      // Read product-specific stock dimensions from data attributes
      this.maxWidth = parseFloat(this.dataset.maxWidth) || 96;
      this.maxLength = parseFloat(this.dataset.maxLength) || 96;

      // Derive grids from actual variant option values (product-specific brackets)
      var widthSet = {};
      var lengthSet = {};
      for (var vi = 0; vi < this.variants.length; vi++) {
        var wVal = parseInt(String(this.variants[vi].option1), 10);
        var lVal = parseInt(String(this.variants[vi].option2), 10);
        if (!isNaN(wVal)) widthSet[wVal] = true;
        if (!isNaN(lVal)) lengthSet[lVal] = true;
      }
      this.widthGrid = Object.keys(widthSet).map(Number).sort(function(a, b) { return a - b; });
      this.lengthGrid = Object.keys(lengthSet).map(Number).sort(function(a, b) { return a - b; });

      // Fallback if no variants parsed
      if (this.widthGrid.length === 0) this.widthGrid = [6];
      if (this.lengthGrid.length === 0) this.lengthGrid = [6];

      // Largest size that actually has a variant to price. The stock-size
      // metafields (maxWidth/maxLength) can exceed this because the variant
      // grid is capped at Shopify's 250-variant limit — so a cut within the
      // metafield bound but beyond the grid would otherwise be silently
      // rounded DOWN to the largest variant and UNDERCHARGED (order #3371).
      this.gridMaxWidth = this.widthGrid[this.widthGrid.length - 1];
      this.gridMaxLength = this.lengthGrid[this.lengthGrid.length - 1];

      // No standard size buttons in C2S-only mode

      if (this.panelsMode) {
        // Panels mode: the ladder is the size list, the variants are the panels.
        // Rungs with no live variant are dropped so a partially built product can
        // never resolve to a panel we cannot price (the old grid path answered
        // that case with a dead configurator).
        this.buildPanelIndex();
        var startPanel = this.pickDefaultPanel();
        this.defaultWidth = startPanel.w;
        this.defaultLength = startPanel.l;
      } else {
        // Default variant: closest to 12×12
        this.defaultWidth = this.widthGrid[0];
        this.defaultLength = this.lengthGrid[0];
        var bestDiff = Infinity;
        for (var wi = 0; wi < this.widthGrid.length; wi++) {
          for (var li = 0; li < this.lengthGrid.length; li++) {
            var diff = Math.abs(this.widthGrid[wi] - 12) + Math.abs(this.lengthGrid[li] - 12);
            if (diff < bestDiff) {
              bestDiff = diff;
              this.defaultWidth = this.widthGrid[wi];
              this.defaultLength = this.lengthGrid[li];
            }
          }
        }
      }
      this.currentWidth = this.defaultWidth;
      this.currentLength = this.defaultLength;
      // The land default IS the quoted ask, so the first render is answered and
      // the hero price is live for crawlers before any human touches an input.
      if (this.panelsMode) {
        this.quotedW = this.defaultWidth;
        this.quotedL = this.defaultLength;
        this.quotedQty = 1;
      }

      // Read product URL for cart
      this.productUrl = this.dataset.productUrl || '';

      // Bind elements
      this.heroPrice = this.querySelector('[data-dps-hero-price]');
      this.heroOrig = this.querySelector('[data-dps-hero-orig]');
      this.heroLabel = this.querySelector('[data-dps-hero-label]');
      this.heroSavings = this.querySelector('[data-dps-hero-savings]');
      this.savingsText = this.querySelector('[data-dps-savings-text]');
      // Sale info shown via hero strikethrough only
      this.stockHint = this.querySelector('[data-dps-stock-hint]');
      this.widthInput = this.querySelector('[data-dps-width-input]');
      this.lengthInput = this.querySelector('[data-dps-length-input]');
      this.widthWrap = this.querySelector('[data-dps-width-wrap]');
      this.lengthWrap = this.querySelector('[data-dps-length-wrap]');
      this.cutHint = this.querySelector('[data-dps-cut-hint]');
      this.oversizeEl = this.querySelector('[data-dps-oversize]');
      this.snapNote = this.querySelector('[data-dps-snap-note]');
      this.areaPreview = this.querySelector('[data-dps-area-preview]');
      this.cutRect = this.querySelector('[data-dps-cut-rect]');
      this.stockLabel = this.querySelector('.dps-stock-label');
      this.areaLabel = this.querySelector('[data-dps-area-label]');
      this.bracketLabel = this.querySelector('[data-dps-bracket-label]');
      this.cutSummary = this.querySelector('[data-dps-cut-summary]');
      this.cutTotal = this.querySelector('[data-dps-cut-total]');
      this.addToCartBtn = this.querySelector('[data-dps-add-cart]');
      this.variantIdInput = this.querySelector('[data-dps-variant-id]');
      this.qtyInput = this.querySelector('[data-dps-qty-hidden]');
      this.lineItemProps = this.querySelector('[data-dps-line-props]');
      this.lineItemPropsBracket = this.querySelector('[data-dps-line-props-bracket]');

      // 3-step layout elements
      this.qtyMinusBtn = this.querySelector('[data-dps-qty-minus]');
      this.qtyPlusBtn = this.querySelector('[data-dps-qty-plus]');
      this.qtyField = this.querySelector('[data-dps-qty-visible]');
      this.qtyDesc = this.querySelector('[data-dps-qty-desc]');
      this.shipWeight = this.querySelector('[data-dps-ship-weight]');

      // Panels-mode slots (absent on grid products, every read is guarded)
      this.panelNoteEl = this.querySelector('[data-dps-panel-note]');
      this.panelUpsellEl = this.querySelector('[data-dps-panel-upsell]');
      this.panelWhyEl = this.querySelector('[data-dps-panel-why]');
      this.panelDiagramEl = this.querySelector('[data-dps-panel-diagram]');
      this.swapEl = this.querySelector('[data-dps-panel-swap]');
      this.minCutEl = this.querySelector('[data-dps-panel-mincut]');
      this.qtyTiersEl = this.querySelector('[data-dps-panel-qty-tiers]');
      // Compact-ask slots (the rev-3 layout; absent on grid products)
      this.getPriceBtn = this.querySelector('[data-dps-get-price]');
      this.quoteUnitLbl = this.querySelector('[data-dps-quote-unit-lbl]');
      this.quoteUnitVal = this.querySelector('[data-dps-quote-unit-val]');
      this.quoteDiscRow = this.querySelector('[data-dps-quote-discount]');
      this.quoteDiscLbl = this.querySelector('[data-dps-quote-discount-lbl]');
      this.quoteDiscVal = this.querySelector('[data-dps-quote-discount-val]');
      this.bracketLineEl = this.querySelector('[data-dps-bracket-line]');
      this.priceHintEl = this.querySelector('[data-dps-price-hint]');
      this.qtyWrap = this.querySelector('[data-dps-qty-wrap]');
      // The button label as Liquid rendered it (EN or FR), restored after a
      // disabled state borrowed the slot for a constraint message.
      if (this.getPriceBtn) this._getPriceLabel = this.getPriceBtn.textContent.trim();

      // Update input max attributes to match product stock
      if (this.panelsMode) {
        // On a rotating ladder a 96 x 12 entry is legal (it normalizes), so the
        // per-axis stock cap would falsely mark it invalid. Cap both fields at the
        // largest rung on either axis and let the assignment do the real gating.
        var rungCap = Math.max(this.maxRungWidth(), this.maxRungLength());
        // Floor both axes at the minimum cut (SPEC 4.0). The attribute is the
        // browser-native hint; resolveAssignment() is the gate that actually
        // blocks, because a number input does not stop anyone typing 2.
        var minCut = this.minCutIn();
        if (this.widthInput) { this.widthInput.max = rungCap; this.widthInput.min = minCut; }
        if (this.lengthInput) { this.lengthInput.max = rungCap; this.lengthInput.min = minCut; }
      } else {
        if (this.widthInput) this.widthInput.max = Math.floor(this.maxWidth);
        if (this.lengthInput) this.lengthInput.max = Math.floor(this.maxLength);
      }

      this.setupEventListeners();
      this.render();

      // Fetch ALL variants via product.js (bypasses Liquid's 100-variant cap)
      if (this.variants.length >= 95) {
        this.fetchAllVariants();
      }
    }

    fetchAllVariants() {
      var self = this;
      var handle = this.dataset.productHandle;
      if (!handle) return;

      fetch('/products/' + handle + '.js')
        .then(function(res) { return res.json(); })
        .then(function(product) {
          if (!product || !product.variants || product.variants.length <= self.variants.length) return;

          // Rebuild variant array in the format the existing code expects
          self.variants = product.variants.map(function(v) {
            return {
              id: v.id,
              price: (v.price / 100).toFixed(2),
              compareAtPrice: v.compare_at_price ? (v.compare_at_price / 100).toFixed(2) : null,
              option1: dpsOptionToEn(v.option1 || ''),
              option2: dpsOptionToEn(v.option2 || ''),
              available: v.available,
              weightGrams: v.weight || 0,
            };
          });

          // Rebuild index and grids
          self.variantIndex = {};
          var widthSet = {};
          var lengthSet = {};
          for (var i = 0; i < self.variants.length; i++) {
            var vw = parseInt(String(self.variants[i].option1), 10);
            var vl = parseInt(String(self.variants[i].option2), 10);
            if (!isNaN(vw) && !isNaN(vl)) {
              self.variantIndex[vw + 'x' + vl] = self.variants[i];
              widthSet[vw] = true;
              lengthSet[vl] = true;
            }
          }
          self.widthGrid = Object.keys(widthSet).map(Number).sort(function(a, b) { return a - b; });
          self.lengthGrid = Object.keys(lengthSet).map(Number).sort(function(a, b) { return a - b; });
          if (self.widthGrid.length) self.gridMaxWidth = self.widthGrid[self.widthGrid.length - 1];
          if (self.lengthGrid.length) self.gridMaxLength = self.lengthGrid[self.lengthGrid.length - 1];
          // A panel ladder is ~12 to 25 variants so this fetch normally never fires
          // there, but if it ever does the panel index has to be rebuilt with it.
          if (self.panelsMode) self.buildPanelIndex();

          // Re-render with full data
          self.render();
        })
        .catch(function() { /* Silently fall back to Liquid data */ });
    }

    /* ══════════════════════ PANELS MODE ══════════════════════ */

    // Index the panel variants by canonical rung key, and drop ladder rungs that
    // have no variant. Panel option values carry a text prefix ("Up to 8.5"
    // width"), so bare parseFloat/parseInt return NaN and would silently empty
    // this index and kill add-to-cart. Extract the first number instead - that
    // reads both generations of naming, and it keeps decimals (10.63 is real,
    // parseInt would floor it to a different rung).
    buildPanelIndex() {
      this.panelIndex = {};
      for (var i = 0; i < this.variants.length; i++) {
        var v = this.variants[i];
        var vw = dpsParseOptionDim(v.option1);
        var vl = dpsParseOptionDim(v.option2);
        if (isNaN(vw) || isNaN(vl)) continue;
        this.panelIndex[dpsPanelKey(vw, vl)] = v;
      }
      // Always filter from the ladder as the metafield gave it, never from an
      // already-filtered copy, so a second pass (fetchAllVariants) can restore
      // rungs the first pass could not see.
      if (!this.ladderFull) this.ladderFull = this.ladder;
      var self = this;
      var live = this.ladderFull.rungs.filter(function (r) {
        return !!self.panelIndex[dpsPanelKey(r.w, r.l)];
      });
      if (live.length) {
        this.ladder = {
          rungs: live,
          rotation: this.ladderFull.rotation,
          tier: this.ladderFull.tier,
          stockMaxWidth: this.ladderFull.stockMaxWidth,
          stockMaxLength: this.ladderFull.stockMaxLength,
        };
      } else {
        console.warn('Dynamic Pricing Sheet: no ladder rung matches a variant; keeping the full ladder');
      }
    }

    // Opening size = the parent variant: 12x12 when the ladder has it, else the
    // nearest panel by the same Manhattan score pickParent() uses in
    // scripts/rollout-sheet-panels.js. Narrow stock has no 12x12 at all (SKU 1888
    // is 10-5/8" wide), and the PDP must open on the same panel the collection
    // card anchors its "from" price to.
    pickDefaultPanel() {
      var rungs = this.ladder.rungs;
      for (var i = 0; i < rungs.length; i++) {
        if (rungs[i].w === 12 && rungs[i].l === 12) return rungs[i];
      }
      var best = rungs[0];
      var bestScore = Infinity;
      for (var j = 0; j < rungs.length; j++) {
        var score = Math.abs(rungs[j].w - 12) + Math.abs(rungs[j].l - 12);
        if (score < bestScore) { bestScore = score; best = rungs[j]; }
      }
      return best;
    }

    maxRungWidth() {
      var m = 0;
      for (var i = 0; i < this.ladder.rungs.length; i++) m = Math.max(m, this.ladder.rungs[i].w);
      return m;
    }
    maxRungLength() {
      var m = 0;
      for (var i = 0; i < this.ladder.rungs.length; i++) m = Math.max(m, this.ladder.rungs[i].l);
      return m;
    }

    // The smallest rung on the ladder. Drawn as the reference panel in the
    // below-minimum state, where no cut resolves but the customer still needs to
    // see what the smallest orderable piece looks like.
    smallestRung() {
      var best = this.ladder.rungs[0];
      for (var i = 1; i < this.ladder.rungs.length; i++) {
        if (this.ladder.rungs[i].area < best.area) best = this.ladder.rungs[i];
      }
      return best;
    }

    minCutIn() {
      return (this.ladder && dpsIsPosFinite(this.ladder.minCutIn))
        ? this.ladder.minCutIn
        : DPS_DEFAULT_MIN_CUT_IN;
    }

    // One classification for the current cut, plus the three mutually exclusive
    // flags render() and addToCart() both read. Called at the top of the panels
    // render. Below-min wins over everything: it is checked before rotation, so
    // a directional 50 x 2 gets the minimum message and NO swap prompt, because
    // offering a swap for a cut we cannot make either way would be a lie.
    resolveAssignment() {
      var res = dpsClassifyCut(this.ladder, { w: this.currentWidth, l: this.currentLength });

      if (res.belowMinCut) {
        this.belowMinInfo = {
          minCutIn: res.minCutIn,
          offending: res.offending || [],
          invalid: !!res.invalid,
        };
        this.oversizeInfo = null;
        this.swapInfo = null;
        return res;
      }
      this.belowMinInfo = null;

      if (res.ok) {
        this.oversizeInfo = null;
        this.swapInfo = null;
        return res;
      }
      if (res.swapFits && !this.swapDeclined) {
        this.swapInfo = res;
        this.oversizeInfo = null;
        return res;
      }
      this.swapInfo = null;
      this.oversizeInfo = {
        reqW: this.currentWidth,
        reqL: this.currentLength,
        overW: this.currentWidth > this.maxRungWidth(),
        overL: this.currentLength > this.maxRungLength(),
      };
      return res;
    }

    // Set the cut from the ladder picker, the upsell, or an accepted axis swap,
    // and mirror it into the W x L fields so the two controls never disagree.
    selectPanel(w, l) {
      this.currentWidth = w;
      this.currentLength = l;
      this.isCustom = true;
      this.snapInfo = null;
      this.swapDeclined = false;
      if (this.widthInput) this.widthInput.value = this.fmtLen(w);
      if (this.lengthInput) this.lengthInput.value = this.fmtLen(l);
      if (this.widthWrap) this.widthWrap.classList.add('focused');
      if (this.lengthWrap) this.lengthWrap.classList.add('focused');
      // Tapping a named size (the hatch, an accepted swap) IS an ask, so it
      // commits the quote directly instead of parking the page in pending.
      if (this.panelsMode) { this.commitQuote(); } else { this.render(); }
    }

    // Grow the cut to the panel it already bills to. With the standard-size
    // picker gone (Aaron, 2026-08-12) this is the ONLY way to land exactly on a
    // standard size, so it has two entry points: the text link and the headroom
    // area of the diagram.
    takeFullPanel() {
      var res = dpsAssignPanel(this.ladder, { w: this.currentWidth, l: this.currentLength });
      var p = dpsPanelOf(res);
      if (p) this.selectPanel(p.w, p.l);
    }

    // Panels-mode price. The panel variant's price IS the price: nothing is
    // computed here, so what the PDP shows is what Shopify charges.
    calcTotalPanels(w, l) {
      var res = dpsAssignPanel(this.ladder, { w: w, l: l });
      var panel = dpsPanelOf(res);
      if (!panel) return null;
      var variant = this.panelIndex[dpsPanelKey(panel.w, panel.l)];
      if (!variant) return null;
      var total = parseFloat(String(variant.price).replace(/,/g, ''));
      return {
        total: total,
        perSqInch: total / panel.area,
        actualArea: w * l,
        bracketArea: panel.area,
        roundedW: panel.w,
        roundedL: panel.l,
        variantId: variant.id,
        panel: panel,
        rotated: !!res.rotated,
        exact: !!res.exact,
      };
    }

    /**
     * What this line is CHARGED at the current quantity.
     *
     * One function, so the total in step 3, the tier table and anything added
     * later cannot disagree with each other the way the staff tool's header and
     * quantity card do (FINAL-PLAN blocker B: $202.20 against $101.10 on the
     * same card). Returns null when there is no price at all.
     */
    quantityCharge(price) {
      if (!price) return null;
      var qty = this.currentQty || 1;
      if (!this.nestTable) {
        return { lineTotal: dpsRoundTo(price.total * qty, 2), perPiece: price.total, basisPoints: 0, tier: null, tiers: [] };
      }
      return dpsChargeFromQuantityTable(this.nestTable, price.panel, price.total, qty);
    }

    /**
     * The quantity tier table.
     *
     * SUPPRESSED when there is no real break to show, and "real" is measured on
     * the DISCOUNT, never on how well the cut fills its panel. A cut that fills
     * its panel exactly is the BEST nesting case, not the worst, because the
     * panel tiles the ladder perfectly: the staff tool's fitPct gate hid the
     * table on 53 real multi-piece lines carrying $985.63, on the two commonest
     * sizes in the book, and quoted $43.08 where the line is $31.06.
     *
     * Suppressing the TABLE never changes the PRICE. Step 3 still charges the
     * tier rate, so a customer under the display threshold pays the discount
     * without being sold a headline about it.
     */
    renderQuantityTiers(price, charge) {
      if (!this.qtyTiersEl) return;
      var el = this.qtyTiersEl;
      el.innerHTML = '';
      el.hidden = true;
      // The display gate (`nest.d`), written by the daily sync. Without it this
      // table lights up the moment a nest block exists, which would make
      // shipping the quantity axis decide "does the tier table ship on the PDP"
      // as a side effect - a separate call, and its standing recommendation is
      // no. Suppressing the table does NOT change the price: step 3 and the
      // cart still charge the tier rate, same as below the discount threshold.
      if (!this.nestTable || this.nestTable.d !== 1) return;
      if (!price || !charge || !charge.tiers || !charge.tiers.length) return;

      var best = 0;
      for (var i = 0; i < charge.tiers.length; i++) {
        var d = 1 - charge.tiers[i].perPiece / price.total;
        if (d > best) best = d;
      }
      // A table headlining 1% is worse than no table.
      if (best < DPS_MIN_TIER_DISCOUNT) return;

      var T = this.panelStr();
      var rows = '';
      for (var j = 0; j < charge.tiers.length; j++) {
        var t = charge.tiers[j];
        var pct = Math.round((1 - t.perPiece / price.total) * 100);
        if (pct < 1) continue;
        var active = charge.tier && charge.tier.quantity === t.quantity;
        rows += '<li class="dps-qt__row' + (active ? ' is-active' : '') + '">' +
          '<span class="dps-qt__qty">' + T.qtyPieces(t.quantity) + '</span>' +
          '<span class="dps-qt__each">' + T.qtyEach(this.fmt(t.perPiece)) + '</span>' +
          '<span class="dps-qt__save">' + T.qtySave(pct) + '</span>' +
          (active ? '<span class="dps-qt__here">' + T.qtyYoureHere + '</span>' : '') +
          '</li>';
      }
      if (!rows) return;

      el.innerHTML =
        '<div class="dps-qt__eyebrow">' + T.qtyEyebrow + '</div>' +
        '<ul class="dps-qt__list">' + rows + '</ul>' +
        '<p class="dps-qt__note">' + T.qtyNote + '</p>';
      el.hidden = false;
    }

    setupEventListeners() {
      // Width input
      this.widthInput.addEventListener('input', () => this.handleCustomInput(false));
      this.lengthInput.addEventListener('input', () => this.handleCustomInput(false));

      // Focus/blur for floating labels
      [
        { input: this.widthInput, wrap: this.widthWrap },
        { input: this.lengthInput, wrap: this.lengthWrap },
      ].forEach(({ input, wrap }) => {
        input.addEventListener('focus', () => {
          wrap.classList.add('focused');
        });
        input.addEventListener('blur', () => {
          if (!input.value) {
            wrap.classList.remove('focused');
          }
          // Finished editing this axis — snap the field to the nearest 1/8".
          this.handleCustomInput(true);
        });
      });

      // Qty stepper (+/- buttons) — bind once
      if (this.qtyMinusBtn) {
        this.qtyMinusBtn.addEventListener('click', () => {
          this.currentQty = Math.max(1, this.currentQty - 1);
          this.render();
        });
      }
      if (this.qtyPlusBtn) {
        this.qtyPlusBtn.addEventListener('click', () => {
          this.currentQty = Math.min(99, this.currentQty + 1);
          this.render();
        });
      }
      if (this.qtyField) {
        this.qtyField.addEventListener('change', (e) => {
          this.currentQty = Math.max(1, Math.min(99, parseInt(e.target.value) || 1));
          e.target.value = this.currentQty;
          this.render();
        });
      }

      // Panels mode: the diagram, the upsell and the axis-swap answer.
      // Delegated so the diagram markup can be re-rendered freely.
      if (this.panelsMode) {
        if (this.panelDiagramEl) {
          // The headroom (the metal between the cut and the panel edge) IS the
          // upsell target. With the picker gone this is the fastest way to land
          // exactly on a standard size, and it is the one part of the diagram
          // where "take the rest of it, it costs nothing" is literally true.
          this.panelDiagramEl.addEventListener('click', (e) => {
            var hit = e.target.closest('[data-dps-take-panel]');
            if (!hit) return;
            e.preventDefault();
            this.takeFullPanel();
          });
        }
        if (this.panelUpsellEl) {
          this.panelUpsellEl.addEventListener('click', (e) => {
            e.preventDefault();
            this.takeFullPanel();
          });
        }
        if (this.swapEl) {
          this.swapEl.addEventListener('click', (e) => {
            var yes = e.target.closest('[data-dps-swap-yes]');
            var no = e.target.closest('[data-dps-swap-no]');
            if (!yes && !no) return;
            e.preventDefault();
            if (yes) {
              this.selectPanel(this.currentLength, this.currentWidth);
            } else {
              this.swapDeclined = true;
              this.render();
            }
          });
        }
        // The gate's commit. Click fires after the qty field's change event, so
        // a value typed and immediately asked for is the value that is quoted.
        if (this.getPriceBtn) {
          this.getPriceBtn.addEventListener('click', (e) => {
            e.preventDefault();
            this.commitQuote();
          });
        }
        // Quantity is part of the ask, so typing in it opens the pending state
        // immediately (the change event alone waits for blur).
        if (this.qtyField) {
          this.qtyField.addEventListener('input', () => {
            var v = parseInt(this.qtyField.value, 10);
            if (!isNaN(v) && v >= 1) {
              this.currentQty = Math.min(99, v);
              this.render();
            }
          });
        }
        if (this.qtyField && this.qtyWrap) {
          this.qtyField.addEventListener('focus', () => this.qtyWrap.classList.add('focused'));
          this.qtyField.addEventListener('blur', () => this.qtyWrap.classList.remove('focused'));
        }
      }

      // Add to cart
      this.addToCartBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.addToCart();
      });
    }

    // Largest cut we'll PRICE online, per axis. Normally this is the variant
    // grid max. But when the grid stops within DPS_GRID_TOLERANCE_IN of the
    // true stock size (a 250-variant-cap artifact — see the const note), we
    // extend up to the stock size and let the price round up to the top
    // bracket, so the full advertised sheet stays orderable. Bigger gaps stay
    // capped at the grid (RFQ route) to keep the order #3371 undercharge guard.
    effMaxAxis(stockMax, gridMax) {
      var stock = Math.floor(stockMax);
      var grid = gridMax || Infinity;
      if (stock > grid && (stock - grid) <= DPS_GRID_TOLERANCE_IN) return stock;
      return Math.min(stock, grid);
    }
    // Panels mode retires DPS_GRID_TOLERANCE_IN and the effMaxAxis workaround
    // entirely: the full sheet is a real rung on each axis, so the largest rung
    // IS the largest priceable cut and there is no sliver to paper over.
    effMaxWidth() {
      if (this.panelsMode) return this.maxRungWidth();
      return this.effMaxAxis(this.maxWidth, this.gridMaxWidth);
    }
    effMaxLength() {
      if (this.panelsMode) return this.maxRungLength();
      return this.effMaxAxis(this.maxLength, this.gridMaxLength);
    }

    // Snap a measurement to the nearest 1/8 inch — the precision we actually cut to.
    snapToEighth(val) { return Math.round(val * 8) / 8; }

    // Format an inch value for display: drop trailing zeros (24 / 24.5 / 24.125).
    fmtLen(val) { return val.toFixed(3).replace(/\.?0+$/, ''); }

    // `commit` is true on a finished change (blur): we then reflect the snapped
    // values back into the fields and show the snap note. During live typing
    // (input, commit=false) we snap silently so price + preview stay accurate.
    handleCustomInput(commit) {
      if (this.panelsMode) return this.handleCustomInputPanels(commit);
      var rawW = parseFloat(this.widthInput.value);
      var rawL = parseFloat(this.lengthInput.value);
      var maxW = this.effMaxWidth();
      var maxL = this.effMaxLength();

      // Snap each typed dimension to the nearest 1/8". Pricing still rounds these
      // UP to the next 1/4" grid value (see roundToGrid / calcTotal), unchanged.
      var w = isNaN(rawW) ? rawW : this.snapToEighth(rawW);
      var l = isNaN(rawL) ? rawL : this.snapToEighth(rawL);
      var movedW = !isNaN(rawW) && Math.abs(w - rawW) > 1e-6;
      var movedL = !isNaN(rawL) && Math.abs(l - rawL) > 1e-6;

      // A dimension is "over" only when the customer typed a real number ABOVE
      // the stock max. Empty / partial / sub-1 entries aren't errors — those
      // round up to the smallest bracket as before.
      var overW = !isNaN(w) && w > maxW;
      var overL = !isNaN(l) && l > maxL;

      // Apply any in-range value so a valid axis still drives price + preview.
      var appliedW = !isNaN(w) && w >= 1 && w <= maxW;
      var appliedL = !isNaN(l) && l >= 1 && l <= maxL;
      if (appliedW) { this.isCustom = true; this.currentWidth = w; }
      if (appliedL) { this.isCustom = true; this.currentLength = l; }

      // Flag the over-max state so render() shows the message + RFQ route and
      // blocks add-to-cart (previously the entry was silently truncated to the
      // last valid digit with no feedback).
      if (overW || overL) {
        this.oversizeInfo = {
          reqW: !isNaN(w) ? w : null,
          reqL: !isNaN(l) ? l : null,
          overW: overW,
          overL: overL,
        };
        this.snapInfo = null; // oversize routes to RFQ; no snap note
      } else {
        this.oversizeInfo = null;
        // Only report axes that actually moved AND were applied — never echo the
        // default value of an axis the customer didn't touch.
        var snapW = movedW && appliedW, snapL = movedL && appliedL;
        this.snapInfo = (commit && (snapW || snapL))
          ? { w: snapW ? w : null, l: snapL ? l : null }
          : null;
      }

      // On a finished change, reflect the snapped values back into the fields.
      if (commit) {
        if (movedW && appliedW) this.widthInput.value = this.fmtLen(w);
        if (movedL && appliedL) this.lengthInput.value = this.fmtLen(l);
      }

      this.render();
    }

    // Panels-mode input handling. Same 1/8" snap and same commit-on-blur note as
    // the grid path. What changes is the gate: there is no per-axis max any more,
    // "too big" means no panel on the ladder contains the cut in either
    // orientation, and resolveAssignment() decides that during render.
    handleCustomInputPanels(commit) {
      var rawW = parseFloat(this.widthInput.value);
      var rawL = parseFloat(this.lengthInput.value);
      var minCut = this.minCutIn();

      // Snap to the nearest 1/8", EXCEPT below the minimum cut. Snapping rounds
      // both ways, so an unguarded 2.99 would quietly become a legal 3 and the
      // PDP would price a cut the shear cannot make, while the staff tool and the
      // engine both still called it below-min. Below the minimum we keep the
      // number the customer typed so the message can be about their own entry.
      var w = (isNaN(rawW) || rawW < minCut) ? rawW : this.snapToEighth(rawW);
      var l = (isNaN(rawL) || rawL < minCut) ? rawL : this.snapToEighth(rawL);
      var movedW = !isNaN(rawW) && Math.abs(w - rawW) > 1e-6;
      var movedL = !isNaN(rawL) && Math.abs(l - rawL) > 1e-6;

      // Empty / partial entries are not errors: they leave the last good value in
      // place. A positive but too-small entry IS applied, so the minimum-cut
      // message can name the offending axis instead of silently ignoring it.
      var appliedW = !isNaN(w) && w > 0;
      var appliedL = !isNaN(l) && l > 0;
      if (appliedW) { this.isCustom = true; this.currentWidth = w; }
      if (appliedL) { this.isCustom = true; this.currentLength = l; }

      // A fresh entry re-opens the swap question the customer may have declined.
      if (appliedW || appliedL) this.swapDeclined = false;

      var snapW = movedW && appliedW, snapL = movedL && appliedL;
      this.snapInfo = (commit && (snapW || snapL))
        ? { w: snapW ? w : null, l: snapL ? l : null }
        : null;

      if (commit) {
        if (movedW && appliedW) this.widthInput.value = this.fmtLen(w);
        if (movedL && appliedL) this.lengthInput.value = this.fmtLen(l);
      }

      this.render();
    }

    // Locale-aware route prefix ('/' on EN, '/fr/' on FR) so JS-built links
    // keep the customer on their language.
    routesRoot() {
      return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    }

    // Build a deep link into the RFQ flow (/pages/quote) that pre-fills this
    // product + the size the customer wanted, so staff see the exact request.
    // `note` is optional; without it the over-max wording is used (original
    // behavior). See parsePrefill()/applyPrefill() in rfq-form.js.
    buildRfqUrl(reqW, reqL, note) {
      var handle = this.dataset.productHandle || '';
      var maxW = this.effMaxWidth();
      var maxL = this.effMaxLength();
      var parts = ['product=' + encodeURIComponent(handle), 'qty=' + (this.currentQty || 1)];
      if (reqW != null && !isNaN(reqW)) parts.push('width=' + encodeURIComponent(reqW));
      if (reqL != null && !isNaN(reqL)) parts.push('length=' + encodeURIComponent(reqL));
      if (note == null) {
        var sizeStr = (reqW != null ? reqW : '?') + '" x ' + (reqL != null ? reqL : '?') + '"';
        var noteTmpl = DPS_T.rfqNote || 'Looking for {size} — larger than the {max} online max.';
        note = noteTmpl.replace('{size}', sizeStr).replace('{max}', maxW + '" x ' + maxL + '"');
      }
      parts.push('note=' + encodeURIComponent(note));
      return this.routesRoot() + 'pages/quote?' + parts.join('&');
    }

    // Show / hide the "larger than our online max" message + RFQ CTA, mark the
    // offending input(s), and toggle the host's oversize class (dims the price
    // + preview so the customer doesn't read a stale size as theirs).
    // Calm note shown when a typed dimension was rounded to the nearest 1/8".
    // Hidden while oversize (that routes to RFQ) or when both axes were on-grid.
    renderSnapNote() {
      var el = this.snapNote;
      if (!el) return;
      // belowMinInfo is always null outside panels mode, so the grid path is
      // unchanged; in panels mode a 1/8" note beside "3 minimum per side" is noise.
      if (this.oversizeInfo || this.belowMinInfo || !this.snapInfo) {
        el.hidden = true;
        el.textContent = '';
        return;
      }
      var parts = [];
      if (this.snapInfo.w != null) parts.push(this.inch(this.fmtLen(this.snapInfo.w)));
      if (this.snapInfo.l != null) parts.push(this.inch(this.fmtLen(this.snapInfo.l)));
      var size = parts.join(' × ');
      el.textContent = (DPS_T.snapNote || 'We cut to the nearest 1/8 inch, so your cut is {size}.').split('{size}').join(size);
      el.hidden = false;
    }

    renderOversize() {
      var el = this.oversizeEl;
      var info = this.oversizeInfo;
      if (!el) return;

      if (!info) {
        el.hidden = true;
        el.innerHTML = '';
        this.classList.remove('dps-is-oversize');
        if (this.widthInput) this.widthInput.classList.remove('dps-input-error');
        if (this.lengthInput) this.lengthInput.classList.remove('dps-input-error');
        return;
      }

      var maxW = this.effMaxWidth();
      var maxL = this.effMaxLength();
      var reqW = info.reqW != null ? info.reqW : this.currentWidth;
      var reqL = info.reqL != null ? info.reqL : this.currentLength;
      var reqStr = this.inch(reqW) + ' × ' + this.inch(reqL);
      var href = this.buildRfqUrl(info.reqW, info.reqL);

      var maxStr = this.inch(maxW) + ' × ' + this.inch(maxL);
      var oversizeTitle = DPS_T.oversizeTitle || 'Larger than our online max';
      var oversizeBodyTmpl = DPS_T.oversizeBody || 'Online orders for this sheet go up to <strong>{max}</strong>. You entered <strong>{req}</strong> — request a quote and our team will see what we can do for a larger cut.';
      var oversizeBody = oversizeBodyTmpl.replace('{max}', maxStr).replace('{req}', reqStr);
      var requestQuoteForTmpl = DPS_T.requestQuoteFor || 'Request a quote for {req}';
      var requestQuoteFor = requestQuoteForTmpl.replace('{req}', reqStr);
      el.innerHTML =
        '<div class="dps-oversize__title">' + oversizeTitle + '</div>' +
        '<div class="dps-oversize__body">' + oversizeBody + '</div>' +
        '<a class="dps-oversize__cta" href="' + href + '">' + requestQuoteFor +
          '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M3 8h10M9 4l4 4-4 4"/></svg></a>';
      el.hidden = false;
      this.classList.add('dps-is-oversize');
      if (this.widthInput) this.widthInput.classList.toggle('dps-input-error', !!info.overW);
      if (this.lengthInput) this.lengthInput.classList.toggle('dps-input-error', !!info.overL);
    }

    // Round UP a dimension to the nearest grid value
    roundToGrid(val, grid) {
      for (var i = 0; i < grid.length; i++) {
        if (grid[i] >= Math.ceil(val)) return grid[i];
      }
      return grid[grid.length - 1];
    }

    // Find the variant for a given width x length combo (O(1) indexed)
    findVariant(w, l) {
      // Panels mode keys on the canonical rung string so decimal rungs survive.
      if (this.panelsMode) return this.panelIndex[dpsPanelKey(w, l)] || undefined;
      var targetW = parseInt(w, 10);
      var targetL = parseInt(l, 10);
      if (isNaN(targetW) || isNaN(targetL)) return undefined;
      return this.variantIndex[targetW + 'x' + targetL] || undefined;
    }

    // Weekly sale banner: countdown to the Sunday that closes the set.
    // saleEndsAt is a plain YYYY-MM-DD; the deadline is 11:59 pm in the
    // customer's local time (the server-side flip is the next Monday run).
    initWeeklyBanner() {
      var banner = this.querySelector('[data-dp-weekly-banner]');
      if (!banner) return;
      var cfg = this.pricingConfig || {};
      var m = cfg.saleAll && cfg.saleEndsAt ? String(cfg.saleEndsAt).match(/^(\d{4})-(\d{2})-(\d{2})$/) : null;
      if (!m) { banner.hidden = true; return; }
      var end = new Date(+m[1], +m[2] - 1, +m[3], 23, 59, 59);
      var out = banner.querySelector('[data-dp-weekly-countdown]');
      var self = this;
      var tick = function() {
        var left = end.getTime() - Date.now();
        if (left <= 0) {
          banner.hidden = true;
          if (self._weeklyTimer) clearInterval(self._weeklyTimer);
          return;
        }
        if (!out) return;
        var d = Math.floor(left / 86400000);
        var h = Math.floor((left % 86400000) / 3600000);
        var mn = Math.floor((left % 3600000) / 60000);
        var tmpl = d > 0
          ? (DPS_T.weeklyLeft || '{d}d {h}h left')
          : (DPS_T.weeklyLeftHours || '{h}h {m}m left');
        out.textContent = tmpl.split('{d}').join(d).split('{h}').join(h).split('{m}').join(mn);
      };
      tick();
      this._weeklyTimer = setInterval(tick, 60000);
    }

    // Classify a preset size: 'promo' | 'volume' | 'both' | 'default'
    classifySize(w, l) {
      if (!this.pricingConfig) return 'default';
      // saleAll = weekly whole-SKU sale: every size carries the discount.
      var isSale = !!this.pricingConfig.saleAll, isVolume = false;
      var saleSizes = this.pricingConfig.saleSizes || [];
      if (!isSale) {
        for (var si = 0; si < saleSizes.length; si++) {
          if (saleSizes[si].w === w && saleSizes[si].l === l) { isSale = true; break; }
        }
      }
      var volTiers = this.pricingConfig.volumeTiers || [];
      for (var vi = 0; vi < volTiers.length; vi++) {
        if (volTiers[vi].w === w && volTiers[vi].l === l && volTiers[vi].tiers && volTiers[vi].tiers.length > 0) {
          isVolume = true; break;
        }
      }
      if (isSale && isVolume) return 'both';
      if (isSale) return 'promo';
      if (isVolume) return 'volume';
      return 'default';
    }

    // Get volume ladder for a specific W x L (exact match or area interpolation)
    getVolumeLadder(w, l) {
      if (!this.pricingConfig) return null;
      var area = w * l;
      var tiers = this.pricingConfig.volumeTiers || [];
      for (var i = 0; i < tiers.length; i++) {
        if (tiers[i].w === w && tiers[i].l === l) return { ladder: tiers[i].tiers, maxQty: tiers[i].maxQty };
      }
      // EXACT KEYS ONLY IN PANELS MODE. customCutCurve samples the LEGACY cut
      // sizes, so on a panels product the nearest-area sample is a different
      // rectangle with a different ladder, and quoting from it is a discount
      // Shopify never applies. The grid path keeps the fallback: there the curve
      // and the cut are the same coordinate system.
      if (this.panelsMode) return null;
      var curve = this.pricingConfig.customCutCurve;
      if (!curve || curve.length === 0) return null;
      var sample = null, minDist = Infinity;
      for (var ci = 0; ci < curve.length; ci++) {
        if (curve[ci].area >= area && curve[ci].area - area < minDist) {
          minDist = curve[ci].area - area; sample = curve[ci];
        }
      }
      if (!sample) sample = curve[curve.length - 1];
      if (!sample || !sample.tiers || sample.tiers.length === 0) return null;
      return { ladder: sample.tiers, maxQty: sample.maxQty, isCustomCurve: true };
    }

    // Build and render the deal legend
    // Sheets rely on hero strikethrough for sale indication
    renderDealHint() { }

    // Show low-stock or OOS indicator for the current size. When the size is
    // out of stock, add a quote CTA that carries the product, the current qty,
    // and the customer's requested width x length into the RFQ form (same
    // pattern as the over-max callout).
    renderStockHint(variant) {
      if (!this.stockHint) return;
      if (!variant) { this.stockHint.innerHTML = ''; return; }
      if (!variant.available) {
        var reqW = this.currentWidth;
        var reqL = this.currentLength;
        var reqStr = this.inch(this.fmtLen(reqW)) + ' \u00d7 ' + this.inch(this.fmtLen(reqL));
        var noteTmpl = DPS_T.oosNote || 'Requested {size} but it shows out of stock online.';
        var href = this.buildRfqUrl(reqW, reqL, noteTmpl.split('{size}').join(reqStr));
        var bodyTmpl = DPS_T.oosQuoteBody || "We can't cut {size} from online stock right now. Request a quote and the team will see what we can do.";
        var ctaTmpl = DPS_T.requestQuoteFor || 'Request a quote for {req}';
        this.stockHint.innerHTML =
          '<div class="dps-stock-hint-oos">' + (DPS_T.oosAtSize || 'Out of stock at this size') + '</div>' +
          '<div class="dps-oversize__body">' + bodyTmpl.split('{size}').join('<strong>' + reqStr + '</strong>') + '</div>' +
          '<a class="dps-oversize__cta" href="' + href + '">' + ctaTmpl.replace('{req}', reqStr) +
            '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M3 8h10M9 4l4 4-4 4"/></svg></a>';
      } else {
        this.stockHint.innerHTML = '';
      }
    }

    // Find the active tier and next tier for a given qty from a ladder
    getActiveTierFromLadder(ladder, qty) {
      if (!ladder || ladder.length === 0) return { active: null, next: ladder ? ladder[0] : null };
      var active = null;
      var next = null;
      for (var i = 0; i < ladder.length; i++) {
        if (qty >= ladder[i].minQty) {
          active = ladder[i];
          next = (i + 1 < ladder.length) ? ladder[i + 1] : null;
        }
      }
      if (!active) next = ladder[0];
      return { active: active, next: next };
    }

    // Render tier progress dots for the volume callout
    renderTierDots(ladder, currentQty) {
      if (!ladder || ladder.length <= 1) return '';
      var html = '<div class="dps-tier-dots">';
      for (var i = 0; i < ladder.length; i++) {
        var tier = ladder[i];
        var reached = currentQty >= tier.minQty;
        var dotClass = 'dps-tier-dot' + (reached ? ' reached' : '');
        html += '<div class="' + dotClass + '">';
        html += '<span class="dps-tier-label">' + tier.label + '</span>';
        html += '<span class="dps-tier-qty">' + tier.minQty + '+</span>';
        html += '</div>';
        if (i < ladder.length - 1) {
          html += '<div class="dps-tier-line' + (currentQty >= ladder[i + 1].minQty ? ' reached' : '') + '"></div>';
        }
      }
      html += '</div>';
      return html;
    }

    // Render volume discount callout
    renderVolumeCallout() {
      var calloutEl = this.querySelector('[data-dps-volume-callout]');
      if (!calloutEl) return;
      calloutEl.className = 'dps-volume-callout';
      calloutEl.innerHTML = '';

      // Panels mode with a quantity table: the tier table IS the quantity story
      // and this callout must stay out of its way. Two ladders on one card is
      // the same defect as two totals on one card, and the legacy one would be
      // the wrong number besides: its keys are the Controls PRESET sizes
      // (6x6, 12x12, 12x24, 24x24, 24x48, 36x48, 48x48, 48x96), which 5,183 of
      // 6,078 panel rungs (85.3%) do not match, so getVolumeLadder falls through
      // to customCutCurve AREA INTERPOLATION and quotes a saving derived from a
      // differently sized preset. Interpolating a quantity price is exactly what
      // this project forbids.
      // PANELS MODE SILENCES THIS UNCONDITIONALLY, nest table or not. The
      // original fence was `panelsMode && nestTable`, which assumed the
      // quantity axis ships in the same window as the flip. It does not: the
      // nest emit gate defaults OFF, so the 2026-08-14 pilot went live with
      // panels_v1 products whose pricing_config still carried the stale GRID
      // volume ladder - and this callout promised "3% at 2+" while the cart
      // Function (correctly, blocker H3) honoured none of it. Aaron caught it
      // on the live pilot the same evening. Volume on panels is the nesting
      // model or nothing (his 08-13 decision); the legacy callout never
      // renders on a panel product again.
      if (this.panelsMode) { calloutEl.style.display = 'none'; return; }

      // Panels mode: the volume ladder is keyed on the BILLED panel, which is the
      // variant Shopify discounts. No panel means no ladder to show.
      var volumeLadder;
      if (this.panelsMode) {
        var vp = this.calcTotalPanels(this.currentWidth, this.currentLength);
        if (!vp) { calloutEl.style.display = 'none'; return; }
        volumeLadder = this.getVolumeLadder(vp.roundedW, vp.roundedL);
      } else {
        volumeLadder = this.getVolumeLadder(
          this.roundToGrid(this.currentWidth, this.widthGrid),
          this.roundToGrid(this.currentLength, this.lengthGrid)
        );
      }
      if (!volumeLadder || !volumeLadder.ladder || volumeLadder.ladder.length === 0) { calloutEl.style.display = 'none'; return; }

      var ladder = volumeLadder.ladder;
      var tierInfo = this.getActiveTierFromLadder(ladder, this.currentQty);
      var sizeStr = this.inch(this.currentWidth) + ' x ' + this.inch(this.currentLength);

      var html = '';
      if (tierInfo.active) {
        var price = this.calcTotal(this.currentWidth, this.currentLength);
        var unitPrice = price ? price.total : 0;
        var savedPerUnit = unitPrice * tierInfo.active.discount;
        var totalSaved = savedPerUnit * this.currentQty;
        var volActiveTmpl = DPS_T.volSaving || 'Volume savings: {label} applied, saving {amount}';
        html = '<div class="dps-vol-active">' + volActiveTmpl.replace('{label}', tierInfo.active.label).replace('{amount}', this.fmt(totalSaved)) + '</div>';

        if (tierInfo.next) {
          var remaining = tierInfo.next.minQty - this.currentQty;
          var volSaveTmpl = DPS_T.volAddToSave || 'Add {n} more {size} pieces to save {label} per cut';
          html += '<div class="dps-vol-next">' + volSaveTmpl.replace('{n}', remaining).replace('{size}', sizeStr).replace('{label}', tierInfo.next.label) + '</div>';
        } else {
          html += '<div class="dps-vol-max">' + (DPS_T.volMax || 'Maximum volume savings applied') + '</div>';
        }

        html += this.renderTierDots(ladder, this.currentQty);
        calloutEl.className = 'dps-volume-callout applied';
        calloutEl.innerHTML = html;
      } else if (tierInfo.next) {
        var remaining = tierInfo.next.minQty - this.currentQty;
        var volUnlockTmpl = DPS_T.volAddToUnlock || 'Add {n} more {size} pieces to unlock {label} bulk savings';
        html = '<div class="dps-vol-next">' + volUnlockTmpl.replace('{n}', remaining).replace('{size}', sizeStr).replace('{label}', tierInfo.next.label) + '</div>';
        html += this.renderTierDots(ladder, this.currentQty);
        calloutEl.className = 'dps-volume-callout approaching';
        calloutEl.innerHTML = html;
      } else {
        calloutEl.style.display = 'none';
      }

      if (html) calloutEl.style.display = '';
    }

    // Calculate the total price for given dimensions
    // For custom sizes: round each dimension UP independently to nearest grid value
    calcTotal(w, l) {
      if (this.panelsMode) return this.calcTotalPanels(w, l);
      var roundedW = this.roundToGrid(w, this.widthGrid);
      var roundedL = this.roundToGrid(l, this.lengthGrid);

      var variant = this.findVariant(roundedW, roundedL);
      if (!variant) return null;

      // variant.price IS the total price for this W x L combo (cut fee already included)
      var total = parseFloat(String(variant.price).replace(/,/g, ''));
      var actualArea = w * l;
      var bracketArea = roundedW * roundedL;
      var perSqInch = total / bracketArea;

      return {
        total: total,
        perSqInch: perSqInch,
        actualArea: actualArea,
        bracketArea: bracketArea,
        roundedW: roundedW,
        roundedL: roundedL,
        variantId: variant.id,
      };
    }

    fmt(n) {
      if (this.isFr) {
        var p = n.toFixed(2).split('.');
        return p[0].replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0') + ',' + p[1] + '\u00a0$';
      }
      return '$' + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    render() {
      // The gate router: every existing call site (inputs, qty, swap, connect)
      // funnels through here, so a mismatched ask can only ever render pending.
      if (this.panelsMode) {
        return this.quoteIsCurrent() ? this.renderPanels() : this.renderPending();
      }
      var price = this.calcTotal(this.currentWidth, this.currentLength);
      if (!price) return;

      // Check for sale (compareAtPrice > price on the matched variant)
      var matchedVar = this.findVariant(price.roundedW, price.roundedL);
      var compareAt = matchedVar && matchedVar.compareAtPrice ? parseFloat(matchedVar.compareAtPrice) : 0;
      var isSale = compareAt > price.total && compareAt > 0;
      var displayPrice = price.total;
      var fullPrice = isSale ? compareAt : price.total;

      var totalWithQty = displayPrice * this.currentQty;
      var savingsPerUnit = fullPrice - displayPrice;
      var discountPct = fullPrice > 0 ? Math.round((1 - displayPrice / fullPrice) * 100) : 0;

      // Hero price with strikethrough
      if (this.heroPrice) this.heroPrice.textContent = this.fmt(displayPrice);
      if (this.heroLabel) this.heroLabel.textContent = (DPS_T.priceFor || 'for') + ' ' + this.inch(this.currentWidth) + ' \u00d7 ' + this.inch(this.currentLength);

      if (savingsPerUnit > 0.005) {
        if (this.heroOrig) { this.heroOrig.textContent = this.fmt(fullPrice); this.heroOrig.classList.add('visible'); }
        if (this.savingsText) {
          var youSaveTmpl = DPS_T.youSave || 'You save {amount} ({pct}% off, sale)';
          this.savingsText.textContent = youSaveTmpl.replace('{amount}', this.fmt(savingsPerUnit)).replace('{pct}', discountPct);
        }
        if (this.heroSavings) this.heroSavings.classList.add('visible');
      } else {
        if (this.heroOrig) this.heroOrig.classList.remove('visible');
        if (this.heroSavings) this.heroSavings.classList.remove('visible');
      }

      // Deal hint + stock hint
      this.renderDealHint();
      var matchedVariant = this.findVariant(price.roundedW, price.roundedL);
      this.renderStockHint(matchedVariant);

      // Cut hint text — use product-specific max dimensions
      var maxWDisplay = Math.floor(this.maxWidth);
      var maxLDisplay = Math.floor(this.maxLength);
      var dpsCutOutOf = DPS_T.cutOutOf || 'cut out of';
      var dpsStockSuffix = DPS_T.stockSuffix || 'stock';
      this.cutHint.innerHTML = '<span class="dps-highlight">' + this.inch(this.currentWidth) + ' \u00d7 ' + this.inch(this.currentLength) + '</span> ' + dpsCutOutOf + ' ' + this.inch(maxWDisplay) + ' \u00d7 ' + this.inch(maxLDisplay) + ' ' + dpsStockSuffix;

      // Visual area preview — container represents the stock sheet
      // Width = horizontal axis, Length = vertical axis
      // Use maxLength (horizontal) / maxWidth (vertical) so landscape sheets render landscape
      // Cap height at 160px to keep the page compact
      var previewMaxH = 220;
      var stockRatio = this.maxLength / this.maxWidth; // e.g. 96/48 = 2 → twice as wide as tall
      var previewW = this.areaPreview.parentElement.offsetWidth || 400;
      var idealH = previewW / stockRatio;
      var finalH = Math.min(idealH, previewMaxH);
      var finalW = finalH * stockRatio;
      this.areaPreview.style.width = Math.min(finalW, previewW) + 'px';
      this.areaPreview.style.height = finalH + 'px';

      // Cut rect as percentage of the stock area
      // Width input → horizontal (length axis), Length input → vertical (width axis)
      // Actually: "Width" = the narrower stock dimension, "Length" = the longer one
      // In the preview: horizontal = maxLength, vertical = maxWidth
      var pctH_stock = Math.max(2, (this.currentWidth / this.maxWidth) * 100);
      var pctW_stock = Math.max(2, (this.currentLength / this.maxLength) * 100);
      this.cutRect.style.width = 'calc(' + pctW_stock + '% - 8px)';
      this.cutRect.style.height = 'calc(' + pctH_stock + '% - 8px)';
      if (pctW_stock > 15 && pctH_stock > 15) {
        this.cutRect.textContent = this.currentWidth + ' \u00d7 ' + this.currentLength;
      } else {
        this.cutRect.textContent = '';
      }

      // Update stock label dynamically
      if (this.stockLabel) {
        this.stockLabel.textContent = this.inch(maxWDisplay) + ' \u00d7 ' + this.inch(maxLDisplay) + ' ' + dpsStockSuffix;
      }

      // Area labels — show cut vs bracket info
      if (this.areaLabel) {
        var cutLabel = this.inch(this.currentWidth) + ' \u00d7 ' + this.inch(this.currentLength) + ' ' + dpsCutOutOf + ' ' + this.inch(this.maxWidth) + ' \u00d7 ' + this.inch(this.maxLength) + ' ' + dpsStockSuffix;
        this.areaLabel.textContent = cutLabel;
      }
      if (this.bracketLabel) this.bracketLabel.textContent = '';

      // Step 2: Qty desc + shipping weight
      if (this.qtyField) this.qtyField.value = this.currentQty;
      if (this.qtyDesc) {
        var sizeLabel = this.inch(this.currentWidth) + ' \u00d7 ' + this.inch(this.currentLength);
        var pieceWord = (this.currentQty > 1 ? (DPS_T.pieceOther || 'pieces') : (DPS_T.pieceOne || 'piece'));
        this.qtyDesc.innerHTML = '<strong>' + this.currentQty + '</strong> x <strong>' + sizeLabel + '</strong> ' + pieceWord;
      }
      // Shipping weight (use matched variant weight)
      var matchedVar = this.findVariant(price.roundedW, price.roundedL);
      var unitWeightLb = matchedVar && matchedVar.weightGrams ? matchedVar.weightGrams / 453.592 : 0;
      var totalWeightLb = unitWeightLb * this.currentQty;
      if (this.shipWeight) {
        this.shipWeight.textContent = totalWeightLb.toFixed(1) + ' lb';
      }

      // Step 3: Summary line (no inline qty input — qty is in Step 2 now)
      var displayPerSqIn = price.total / price.bracketArea;
      this.cutSummary.textContent = this.currentQty + ' x ' + this.inch(this.currentWidth) + ' \u00d7 ' + this.inch(this.currentLength) + ' @ ' + this.fmt(displayPerSqIn) + (DPS_T.perSqIn || '/sq in');

      // Total
      this.cutTotal.textContent = this.fmt(totalWithQty);

      // Update hidden form fields
      this.variantIdInput.value = price.variantId;
      // Cart qty = cut count (NOT area)
      this.qtyInput.value = this.currentQty;

      // Line item properties — your cut + price bracket
      if (this.lineItemProps) {
        this.lineItemProps.value = this.currentWidth + '" \u00d7 ' + this.currentLength + '"';
      }
      if (this.lineItemPropsBracket) {
        this.lineItemPropsBracket.value = price.roundedW + '" \u00d7 ' + price.roundedL + '"';
      }

      // Reset add to cart button state
      this.addToCartBtn.textContent = DPS_T.addToCart || 'Add to Cart';
      this.addToCartBtn.classList.remove('added');
      this.addToCartBtn.disabled = false;

      // Over-max entry: show the RFQ message and block add-to-cart so the
      // customer can't buy a size other than the one they typed.
      this.renderOversize();
      this.renderSnapNote();
      if (this.oversizeInfo) {
        this.addToCartBtn.disabled = true;
        var enterTmpl = DPS_T.enterSizeUpTo || 'Enter a size up to {max}';
        this.addToCartBtn.textContent = enterTmpl.replace('{max}', this.inch(this.effMaxWidth()) + ' × ' + this.inch(this.effMaxLength()));
      }

      // Volume discount callout
      this.renderVolumeCallout();
    }

    /* ── panels-mode display helpers ── */

    panelStr() { return this.isFr ? DPS_PANEL_STR.fr : DPS_PANEL_STR.en; }

    // A ladder rung for display: `12"` / `10.63"`, or `12 po` / `10,63 po` in FR.
    rungLabel(v) {
      var s = dpsFormatRung(v);
      return this.isFr ? s.replace('.', ',') + ' po' : s + '"';
    }
    // Any size on screen: English prints exactly what it always did (`12"`),
    // French says `12 po` with a decimal comma. Cart properties never use this:
    // order entry reads their `"`.
    inch(v) {
      var s = String(v);
      return this.isFr ? s.replace('.', ',') + ' po' : s + '"';
    }
    // A typed cut for display. Cuts land on 1/8ths so they keep fmtLen's precision.
    cutLabel(v) {
      var s = this.fmtLen(v);
      return this.isFr ? s.replace('.', ',') + ' po' : s + '"';
    }
    // ONE size separator everywhere in panels mode: U+00D7, the sign the rest of
    // the configurator and the cart properties already use. SPEC section 9 was
    // updated to match (2026-08-12), so prose and chrome no longer diverge.
    sizeLabel(a, b, isRung) {
      var f = isRung ? this.rungLabel.bind(this) : this.cutLabel.bind(this);
      return f(a) + ' × ' + f(b);
    }

    /* ── THE GET PRICE GATE (Aaron, 2026-08-14 design pass) ──────────────────
       Typing never moves the money. The quoted ask (quotedW/L/Qty) is the only
       thing the hero, the answer rows, the total and the drawing render from;
       editing any input mismatches it and the page goes quiet (renderPending)
       until Get price commits. Two hard contracts:
         1. LAND IS ALWAYS PRICED. The default panel is seeded as the quoted ask
            before the first render, so crawlers and the feed always see a price.
         2. ONE RED BUTTON. Get price and Add to cart share the slot and exactly
            one is visible: unpriced asks cannot reach the cart at all.        */
    quoteIsCurrent() {
      return this.quotedW === this.currentWidth
        && this.quotedL === this.currentLength
        && this.quotedQty === this.currentQty;
    }

    commitQuote() {
      // Belt to the disabled button's suspender: an ask the classifier refuses
      // (below-min, oversize, unanswered swap) never becomes the quote.
      var res = dpsClassifyCut(this.ladder, { w: this.currentWidth, l: this.currentLength });
      if (!res.ok) { this.render(); return; }
      this.quotedW = this.currentWidth;
      this.quotedL = this.currentLength;
      this.quotedQty = this.currentQty;
      this.renderPanels();
    }

    /**
     * The pending state. Calm, not broken: the hero and the last answer dim and
     * fold (CSS off dps-is-pending), the echo restates the full ask with its
     * quantity, one hint says what to do, and validation renders LIVE so the
     * customer knows before asking. No money surface is touched here.
     */
    renderPending() {
      var T = this.panelStr();
      this.classList.add('dps-is-pending');
      this.resolveAssignment();
      var cutStr = this.sizeLabel(this.currentWidth, this.currentLength, false);

      var stockW = this.ladder.stockMaxWidth != null ? this.ladder.stockMaxWidth : this.maxWidth;
      var stockL = this.ladder.stockMaxLength != null ? this.ladder.stockMaxLength : this.maxLength;
      if (this.cutHint) {
        this.cutHint.innerHTML = '<span class="dps-highlight">' + this.currentQty + ' × ' + cutStr + '</span> ' +
          (DPS_T.cutOutOf || 'cut out of') + ' ' + this.sizeLabel(stockW, stockL, true) + ' ' +
          (DPS_T.stockSuffix || 'stock');
      }
      if (this.priceHintEl) this.priceHintEl.textContent = T.heroHint(this.currentQty, cutStr);
      if (this.qtyField) this.qtyField.value = this.currentQty;

      // Validation, live. Same ordering law as renderPanels: the min-cut note
      // renders AFTER renderOversize, which clears the input error classes.
      this.renderSwapPrompt();
      this.renderOversize();
      this.renderMinCutNote();
      this.renderSnapNote();
      this.renderStockHint(null);

      // The slot: Get price only. Disabled while the ask is invalid, and the
      // label borrows the constraint (the inline message carries the detail).
      if (this.addToCartBtn) this.addToCartBtn.hidden = true;
      if (this.getPriceBtn) {
        this.getPriceBtn.hidden = false;
        if (this.belowMinInfo) {
          this.getPriceBtn.disabled = true;
          this.getPriceBtn.textContent = T.minCutAtc(this.rungLabel(this.belowMinInfo.minCutIn));
        } else if (this.oversizeInfo) {
          this.getPriceBtn.disabled = true;
          var enterTmpl = DPS_T.enterSizeUpTo || 'Enter a size up to {max}';
          this.getPriceBtn.textContent = enterTmpl.replace('{max}', this.sizeLabel(this.maxRungWidth(), this.maxRungLength(), true));
        } else if (this.swapInfo) {
          // The swap box's two buttons are the real actions here.
          this.getPriceBtn.disabled = true;
          this.getPriceBtn.textContent = this._getPriceLabel || 'Get price';
        } else {
          this.getPriceBtn.disabled = false;
          this.getPriceBtn.textContent = this._getPriceLabel || 'Get price';
        }
      }
    }

    /**
     * Panels-mode render. Deliberately a separate function from render(): the grid
     * path stays byte-for-byte what it was, and this one is free to keep drawing
     * when no panel resolves (the grid path bails, which is how a sparse ladder
     * would otherwise produce a silently dead configurator).
     */
    renderPanels() {
      var T = this.panelStr();
      // The answered state: the quoted ask is current, so the money surfaces
      // un-dim and Add to cart takes the slot back from Get price.
      this.classList.remove('dps-is-pending');
      if (this.getPriceBtn) this.getPriceBtn.hidden = true;
      if (this.addToCartBtn) this.addToCartBtn.hidden = false;
      var assignment = this.resolveAssignment();
      var panel = dpsPanelOf(assignment);
      var price = panel ? this.calcTotalPanels(this.currentWidth, this.currentLength) : null;
      var cutStr = this.sizeLabel(this.currentWidth, this.currentLength, false);
      var panelStr = panel ? this.sizeLabel(panel.w, panel.l, true) : '';
      var isFullPanel = !!(price && price.exact);

      // Hero price + label
      var matchedVar = panel ? this.findVariant(panel.w, panel.l) : null;
      var compareAt = matchedVar && matchedVar.compareAtPrice ? parseFloat(matchedVar.compareAtPrice) : 0;
      var displayPrice = price ? price.total : 0;
      var isSale = price && compareAt > price.total && compareAt > 0;
      var fullPrice = isSale ? compareAt : displayPrice;
      var savingsPerUnit = fullPrice - displayPrice;
      var discountPct = fullPrice > 0 ? Math.round((1 - displayPrice / fullPrice) * 100) : 0;

      // The hero is the CHARGED per-piece: with a quantity discount active the
      // variant price is a number nobody on this line pays, and the mockup of
      // record shows the discounted figure (displayed = charged).
      var charge = this.quantityCharge(price);
      if (this.heroPrice) {
        this.heroPrice.textContent = (price && charge) ? this.fmt(charge.perPiece) : (price ? this.fmt(displayPrice) : '--');
      }
      // The price belongs to THEIR cut (Aaron, 2026-08-14): "for your 7" × 20"".
      // An exact-rung ask (the land default included) names the rung plainly.
      if (this.heroLabel) {
        this.heroLabel.textContent = (panel && !isFullPanel)
          ? T.heroForCut(cutStr)
          : (DPS_T.priceFor || 'for') + ' ' + (panel ? panelStr : cutStr);
      }
      // The bracket, demoted to its one muted line. Empty on an exact-rung ask.
      if (this.bracketLineEl) {
        this.bracketLineEl.textContent = (panel && !isFullPanel) ? T.bracketLine(panelStr) : '';
      }
      if (savingsPerUnit > 0.005) {
        if (this.heroOrig) { this.heroOrig.textContent = this.fmt(fullPrice); this.heroOrig.classList.add('visible'); }
        if (this.savingsText) {
          var youSaveTmpl = DPS_T.youSave || 'You save {amount} ({pct}% off, sale)';
          this.savingsText.textContent = youSaveTmpl.replace('{amount}', this.fmt(savingsPerUnit)).replace('{pct}', discountPct);
        }
        if (this.heroSavings) this.heroSavings.classList.add('visible');
      } else {
        if (this.heroOrig) this.heroOrig.classList.remove('visible');
        if (this.heroSavings) this.heroSavings.classList.remove('visible');
      }

      this.renderStockHint(matchedVar);

      // Cut hint (unchanged wording, stock dims from the ladder)
      var stockW = this.ladder.stockMaxWidth != null ? this.ladder.stockMaxWidth : this.maxWidth;
      var stockL = this.ladder.stockMaxLength != null ? this.ladder.stockMaxLength : this.maxLength;
      var stockStr = this.sizeLabel(stockW, stockL, true);
      var dpsCutOutOf = DPS_T.cutOutOf || 'cut out of';
      var dpsStockSuffix = DPS_T.stockSuffix || 'stock';
      if (this.cutHint) {
        this.cutHint.innerHTML = '<span class="dps-highlight">' + cutStr + '</span> ' +
          dpsCutOutOf + ' ' + stockStr + ' ' + dpsStockSuffix;
      }

      // The panel note and the free-upsize link are GONE (Aaron, 2026-08-14):
      // the note claimed we fabricate from a pre-sized panel, and the upsell
      // handed away resellable offcut metal. The bracket line in the hero and
      // the drawing's hatch are the whole bracket story now. DPS_PANEL_STR
      // keeps note/upsell so the SPEC section 9 copy pins stay green.
      if (this.panelWhyEl) this.panelWhyEl.hidden = !panel;

      this.renderSwapPrompt();
      this.renderPanelDiagram(panel, isFullPanel);

      // Stock context. The diagram is panel-first now, so the sheet the panel
      // comes out of survives as this one caption line and nowhere else.
      if (this.areaLabel) this.areaLabel.textContent = cutStr + ' ' + dpsCutOutOf + ' ' + stockStr + ' ' + dpsStockSuffix;
      // "Billed as ..." is retired in panels mode: the diagram states the panel
      // graphically and labels it, so the line was a fourth reading of the same
      // number under a hero, a note and an upsell that all already carry it.
      if (this.bracketLabel) this.bracketLabel.textContent = '';

      // Step 2: qty + weight (quantity is pieces, same as today)
      if (this.qtyField) this.qtyField.value = this.currentQty;
      if (this.qtyDesc) {
        var pieceWord = (this.currentQty > 1 ? (DPS_T.pieceOther || 'pieces') : (DPS_T.pieceOne || 'piece'));
        this.qtyDesc.innerHTML = '<strong>' + this.currentQty + '</strong> x <strong>' +
          (panel ? panelStr : cutStr) + '</strong> ' + pieceWord;
      }
      if (this.shipWeight) {
        var unitWeightLb = matchedVar && matchedVar.weightGrams ? matchedVar.weightGrams / 453.592 : 0;
        this.shipWeight.textContent = (unitWeightLb * this.currentQty).toFixed(1) + ' lb';
      }

      // Step 3. No $/sq-in here: on a panel that number reads as a markup on the
      // customer's cut, which is the opposite of what it means.
      if (this.cutSummary) {
        if (panel) {
          this.cutSummary.textContent = isFullPanel
            ? T.summaryFull(this.currentQty, panelStr)
            : T.summary(this.currentQty, cutStr, panelStr);
        } else if (this.belowMinInfo) {
          // "no standard panel covers this" would be a lie here: no panel is the
          // symptom, the minimum is the cause.
          this.cutSummary.textContent =
            T.summaryBelowMin(this.currentQty, cutStr, this.rungLabel(this.belowMinInfo.minCutIn));
        } else {
          this.cutSummary.textContent = T.summaryNone(this.currentQty, cutStr);
        }
      }
      // THE LINE TOTAL. Straight price x quantity is only right when no
      // quantity table exists; once one does, the cart charges the tier rate and
      // a multiplied unit price would quote the customer HIGH on every
      // multi-piece line. Same arithmetic as the Function, off the same stored
      // rate, so this number and the cart's are the same number.
      // (charge computed above, beside the hero, so the two cannot diverge)
      if (this.cutTotal) this.cutTotal.textContent = charge ? this.fmt(charge.lineTotal) : '--';

      // The answer rows (compact ask): per piece at the CHARGED rate, and the
      // auto-applied volume discount as its own row. The discount row renders
      // ONLY off a real nest table's basis points - the same stored rate the
      // Function charges - so displayed equals charged (the callout-leak law).
      if (this.quoteUnitLbl) this.quoteUnitLbl.textContent = T.perPieceLbl(cutStr);
      if (this.quoteUnitVal) {
        this.quoteUnitVal.textContent = (charge && price)
          ? this.fmt(charge.perPiece) + (this.currentQty > 1 ? ' × ' + this.currentQty : '')
          : '--';
      }
      if (this.quoteDiscRow) {
        var showDisc = !!(charge && charge.basisPoints > 0);
        this.quoteDiscRow.hidden = !showDisc;
        if (showDisc) {
          var pctStr = dpsFormatRung(charge.basisPoints / 100);
          if (this.isFr) pctStr = pctStr.replace('.', ',');
          if (this.quoteDiscLbl) this.quoteDiscLbl.textContent = T.discountLbl(this.currentQty);
          if (this.quoteDiscVal) this.quoteDiscVal.textContent = '−' + pctStr + (this.isFr ? ' %' : '%');
        }
      }

      if (price) {
        this.variantIdInput.value = price.variantId;
        this.qtyInput.value = this.currentQty;
        if (this.lineItemProps) this.lineItemProps.value = this.currentWidth + '" × ' + this.currentLength + '"';
        if (this.lineItemPropsBracket) this.lineItemPropsBracket.value = price.roundedW + '" × ' + price.roundedL + '"';
      }

      this.addToCartBtn.textContent = DPS_T.addToCart || 'Add to Cart';
      this.addToCartBtn.classList.remove('added');
      this.addToCartBtn.disabled = false;

      this.renderQuantityTiers(price, charge);

      this.renderOversize();
      // AFTER renderOversize: that one clears the input error classes when there
      // is no oversize, which would wipe the below-min axis marking.
      this.renderMinCutNote();
      this.renderSnapNote();
      if (this.belowMinInfo) {
        // Below the minimum the ask is "change the number", not "get a quote",
        // so the button says the constraint rather than pointing at the max.
        this.addToCartBtn.disabled = true;
        this.addToCartBtn.textContent = T.minCutAtc(this.rungLabel(this.belowMinInfo.minCutIn));
      } else if (this.oversizeInfo || this.swapInfo || !price) {
        this.addToCartBtn.disabled = true;
        var enterTmpl = DPS_T.enterSizeUpTo || 'Enter a size up to {max}';
        this.addToCartBtn.textContent = enterTmpl.replace('{max}', this.sizeLabel(this.maxRungWidth(), this.maxRungLength(), true));
      }

      this.renderVolumeCallout();
    }

    /* ── the cut diagram ──────────────────────────────────────────────────────
       Aaron, 2026-08-12: "there is no need for the standard length buttons within
       the PDPs, you can do everything visually with the cut outline to highlight
       how much they are paying for." So the picker is gone and this drawing is
       the whole standard-size story. One thing has to land without reading a
       word: you are paying for THIS much metal, and your piece comes out of it.

       THE HEADROOM FRAME (three treatments were built, Aaron chose this one).

       The BILLED PANEL is the box: a hard-edged frame, because the frame is the
       boundary of what the money buys. The customer's cut is the solid block
       inside it. The metal between the two is hatched, and the hatch is the load
       bearing idea: it has to read as "included, you can still use this", never
       as "unavailable". Three things do that work together, and an edit that
       drops one of them breaks the sentence the drawing is making:

         1. the CUT is the faintly red block (Aaron's 2026-08-14 polish pass)
            and the hatch stays NEUTRAL grey, so the headroom reads as included
            metal rather than as a dead zone or an error,
         2. it carries a permanently visible, underlined label ("Same price up
            to here") which makes it read as an offer rather than as a state,
         3. hovering it fills it with the cut's own tint, previewing the panel
            as one whole piece of THEIR metal, which is exactly what tapping does.

       The headroom IS the tap target for the upsell. With the picker gone the
       upsell is the only way to land exactly on a standard size, so it gets two
       entry points: the text link, and the metal itself.

       Scale is proportional with a floor, and the PANEL is what frames it. That
       is what makes the extremes survivable: the smallest containing panel is
       never more than four times the cut on an axis, so a 3 x 3 cut fills a
       quarter of its panel instead of being the speck it is against 48 x 96.
       ──────────────────────────────────────────────────────────────────────── */

    // Attribute-safe text. Sizes carry inch marks, so anything landing in an
    // aria-label or title has to escape them.
    attrEsc(s) {
      return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
    }

    // Box geometry, shared by every diagram state. Proportional where it fits,
    // floored where the aspect ratio would flatten the shape out of existence: a
    // 3 x 96 panel is 32:1 and would otherwise draw twelve pixels tall.
    diagramBox(w, l) {
      var avail = (this.panelDiagramEl && this.panelDiagramEl.clientWidth) || 0;
      if (avail < 140) avail = 400; // not laid out yet, or a hidden ancestor
      var maxH = 150, minH = 54;
      var ratio = (l > 0 && w > 0) ? (l / w) : 1;
      var h = avail / ratio;
      if (h > maxH) h = maxH;
      if (h < minH) h = minH;
      return { w: Math.round(Math.min(avail, h * ratio)), h: Math.round(h) };
    }

    renderPanelDiagram(panel, isFullPanel) {
      var el = this.panelDiagramEl;
      if (!el) return;
      var T = this.panelStr();
      var stockW = this.ladder.stockMaxWidth != null ? this.ladder.stockMaxWidth : this.maxWidth;
      var stockL = this.ladder.stockMaxLength != null ? this.ladder.stockMaxLength : this.maxLength;

      // Frame, nested shape, and what each is called. Three states, one drawing.
      var frame, inner, eyebrow, frameLabel, innerLabel, cls;
      if (panel) {
        // The cut is drawn in the orientation we will actually cut it, so a
        // rotated entry never escapes its own panel on screen.
        var cw = this.currentWidth, cl = this.currentLength;
        if (this.ladder.rotation && cw > cl) { var t = cw; cw = cl; cl = t; }
        frame = panel; inner = { w: cw, l: cl };
        eyebrow = T.payFor;
        frameLabel = this.sizeLabel(panel.w, panel.l, true);
        innerLabel = this.sizeLabel(this.currentWidth, this.currentLength, false);
        cls = 'dps-pdiag' + (isFullPanel ? ' is-full' : '');
      } else if (this.belowMinInfo) {
        // Nothing resolves, but the customer still needs to see the floor: the
        // smallest panel we sell, with the minimum cut squared off inside it.
        var min = this.belowMinInfo.minCutIn;
        var small = this.smallestRung();
        frame = small; inner = { w: min, l: min };
        eyebrow = T.smallestTag;
        frameLabel = this.sizeLabel(small.w, small.l, true);
        innerLabel = T.minLabel(this.rungLabel(min));
        cls = 'dps-pdiag is-ghost';
      } else {
        // Oversize, or an unanswered swap: the panel does not exist, so the sheet
        // is the frame again and the ask overflows it. That IS the message.
        frame = { w: stockW, l: stockL };
        inner = { w: Math.min(this.currentWidth, stockW), l: Math.min(this.currentLength, stockL) };
        eyebrow = T.stockTag;
        frameLabel = this.sizeLabel(stockW, stockL, true);
        innerLabel = this.sizeLabel(this.currentWidth, this.currentLength, false);
        cls = 'dps-pdiag is-over';
      }

      var box = this.diagramBox(frame.w, frame.l);
      // The cut is sized as a PERCENTAGE of the panel, not in pixels. Percentages
      // resolve against the panel's padding box, so the cut can never draw past
      // the shape it is supposed to sit inside even when max-width clamps the
      // panel a pixel or two below the box we asked for. That escape is the one
      // thing this drawing must never do. Pixels are still used, below, to decide
      // which LABEL fits, where being a pixel out costs nothing.
      var pctW = Math.min(100, (inner.l / frame.l) * 100);
      var pctH = Math.min(100, (inner.w / frame.w) * 100);
      var cutW = Math.max(12, Math.round(box.w * pctW / 100));
      var cutH = Math.max(12, Math.round(box.h * pctH / 100));
      var roomW = box.w - cutW, roomH = box.h - cutH;

      // MINIMUM LEGIBLE HATCH. A nearly-full cut leaves a few pixels of headroom,
      // and a few pixels of 8px-period hatching between two 1px borders does not
      // read as "metal you have already paid for". It reads as a misaligned
      // border: a rendering artefact, not an offer. So under the threshold the
      // drawing rounds to the honest simplification and shows the panel as bought
      // outright, which is what a cut within a hair of its panel IS.
      //
      // Only the DRAWING rounds. The panel note, the upsell link and the cart
      // still carry the real cut and the real panel, so nothing the customer can
      // act on is rounded, and the text upsell stays as the way to actually take
      // the last eighth of an inch.
      //
      // The threshold is a pixel count on purpose: the artefact is a pixel-level
      // phenomenon. The hatch repeats every 8px along the gradient, about 5.7px
      // across it, so 10px is the first width that shows a full light-dark-light
      // cycle once the cut's own 1px border is out of it.
      var hatchReads = Math.max(roomW, roomH) >= DPS_MIN_HATCH_PX;
      var drawFull = isFullPanel || (panel && !hatchReads);
      if (drawFull) { pctW = 100; pctH = 100; cutW = box.w; cutH = box.h; roomW = 0; roomH = 0; }
      if (drawFull) cls = 'dps-pdiag is-full';

      // Label fit, measured in pixels rather than guessed in percentages: a label
      // only prints where it actually fits, and the eyebrow above still carries
      // the meaning when nothing fits inside the shapes. Nothing here ellipsizes:
      // a half-read label is worse than no label (MOBILE LAW).
      var showInner = cutW >= 72 && cutH >= 24;
      var showTag = !drawFull && cutH >= 34 && cutW >= 116;
      // The headroom label goes wherever the headroom actually IS. Normally that
      // is the strip under the cut, which has to be deep enough to clear it (the
      // cut paints over the label) and wide enough to hold the sentence: 21
      // characters at 9.5px is about 105px, and the French is not far behind.
      // When the cut runs the full width of the panel the headroom is a
      // right-hand strip instead, which is the shape a 3 x 3 on a 3 x 6 panel
      // makes, so the label moves there rather than vanishing. If neither strip
      // fits it, it is dropped rather than ellipsized: the panel note above still
      // says "Same price up to 12 x 12" in words, and a half-read label is worse
      // than none (MOBILE LAW).
      var roomBottom = !drawFull && roomH >= 18 && box.w >= 120;
      var roomSide = !drawFull && !roomBottom && roomW >= 120 && box.h >= 26;
      var showRoom = roomBottom || roomSide;

      var h = '';
      h += '<div class="dps-pdiag__head">' +
        '<span class="dps-pdiag__eyebrow">' + eyebrow + '</span>' +
        '<span class="dps-pdiag__size">' + frameLabel + '</span>' +
        '</div>';
      h += '<div class="dps-pdiag__stage">';
      h += '<div class="dps-pdiag__panel" style="width:' + box.w + 'px;height:' + box.h + 'px">';

      if (panel && !drawFull) {
        var take = T.upsell(frameLabel);
        h += '<button type="button" class="dps-pdiag__room" data-dps-take-panel' +
          ' aria-label="' + this.attrEsc(take) + '" title="' + this.attrEsc(take) + '">' +
          (showRoom
            ? '<span class="dps-pdiag__room-tag' + (roomSide ? ' is-side' : '') + '"' +
              (roomSide ? ' style="max-width:' + (roomW - 12) + 'px"' : '') + '>' + T.headroom + '</span>'
            : '') +
          '</button>';
      }

      h += '<div class="dps-pdiag__cut" style="width:' + pctW + '%;height:' + pctH + '%">' +
        (showTag ? '<span class="dps-pdiag__cut-tag">' + T.yourCutTag + '</span>' : '') +
        (showInner ? '<span class="dps-pdiag__cut-size">' + innerLabel + '</span>' : '') +
        '</div>';

      h += '</div></div>';

      el.className = cls;
      el.innerHTML = h;
      this.dropHalfReadLabels(el);
    }

    // The pixel gates above decide whether a label PROBABLY fits, from character
    // counts measured on the English strings. This is the guarantee, taken after
    // layout in whatever language and font the browser actually used: anything
    // that would ellipsize or clip is removed instead. Nothing in this drawing is
    // allowed to half-read (MOBILE LAW), and the eyebrow, the head row and the
    // panel note above still carry every fact the drawing drops.
    //
    // Found by the French sweep: "Minimum 3 po" is wider than the English
    // "Min 3"" and ellipsized inside the below-minimum reference drawing, which
    // no character count in the render path could have predicted.
    dropHalfReadLabels(el) {
      if (!el || !el.querySelectorAll) return;
      var tags = el.querySelectorAll('.dps-pdiag__cut-size, .dps-pdiag__cut-tag, .dps-pdiag__room-tag');
      for (var i = 0; i < tags.length; i++) {
        var t = tags[i];
        // No layout (jsdom, a display:none ancestor, pre-paint): leave it alone
        // rather than deleting a label on a zero measurement.
        if (!t.clientWidth) continue;
        if (t.scrollWidth - t.clientWidth > 1 && t.parentNode) t.parentNode.removeChild(t);
      }
    }

    // SPEC 4.0: under the minimum the shear bends the metal, so the cut is
    // impossible rather than expensive. Explain inline, mark the offending axis,
    // and deliberately offer NO quote link: routing here to the RFQ form would
    // invite a request nobody can fulfil.
    renderMinCutNote() {
      var el = this.minCutEl;
      var info = this.belowMinInfo;
      if (!el) return;

      if (!info) {
        el.hidden = true;
        el.textContent = '';
        if (this.widthInput) this.widthInput.classList.remove('dps-input-error');
        if (this.lengthInput) this.lengthInput.classList.remove('dps-input-error');
        return;
      }

      el.textContent = this.panelStr().minCut(this.rungLabel(info.minCutIn));
      el.hidden = false;
      var off = info.offending || [];
      if (this.widthInput) this.widthInput.classList.toggle('dps-input-error', off.indexOf('w') > -1);
      if (this.lengthInput) this.lengthInput.classList.toggle('dps-input-error', off.indexOf('l') > -1);
    }

    // SPEC 4.5: on a rotation-locked SKU a cut that fits nothing as entered but
    // fits flipped gets asked about, not bounced. Declining falls through to RFQ.
    renderSwapPrompt() {
      var el = this.swapEl;
      if (!el) return;
      if (!this.swapInfo || !this.swapInfo.swap) {
        el.hidden = true;
        el.innerHTML = '';
        return;
      }
      var T = this.panelStr();
      var sw = this.swapInfo.swap.cut;
      el.innerHTML =
        '<div class="dps-swap__title">' + T.swapTitle + '</div>' +
        '<div class="dps-swap__body">' +
          T.swapBody(this.sizeLabel(this.currentWidth, this.currentLength, false), this.cutLabel(sw.w), this.cutLabel(sw.l)) +
        '</div>' +
        '<div class="dps-swap__actions">' +
          '<button type="button" class="dps-swap__yes" data-dps-swap-yes>' + T.swapYes(this.sizeLabel(sw.w, sw.l, false)) + '</button>' +
          '<button type="button" class="dps-swap__no" data-dps-swap-no>' + T.swapNo + '</button>' +
        '</div>';
      el.hidden = false;
    }

    async addToCart() {
      // Panels mode, FIRST gate. Below the minimum cut we stop dead: no cart
      // line, and NO redirect to /pages/quote, because the shear cannot make it
      // at any price and a quote request would waste the customer's time and
      // ours. The inline message already names the one thing that fixes it.
      if (this.panelsMode && this.belowMinInfo) return;
      // The Get Price gate: an unpriced ask can never reach the cart. The
      // button is hidden while pending; this is the belt to that suspender.
      if (this.panelsMode && !this.quoteIsCurrent()) return;
      // Never add an over-max size — route to RFQ instead.
      if (this.oversizeInfo) { window.location.href = this.buildRfqUrl(this.oversizeInfo.reqW, this.oversizeInfo.reqL); return; }
      // An unanswered axis-swap question is not a purchase either. Ask first.
      if (this.panelsMode && this.swapInfo) return;
      var price = this.calcTotal(this.currentWidth, this.currentLength);
      if (!price) {
        if (this.panelsMode) window.location.href = this.buildRfqUrl(this.currentWidth, this.currentLength);
        return;
      }
      // Safety net against undercharge (order #3371): never sell a cut LARGER
      // than what we'll price online. Gate on the effective priceable max, not
      // the raw bracket — within the grid-tolerance sliver we deliberately
      // round a near-full sheet down to the top bracket (e.g. 48"→47").
      // Panels mode has no per-axis gate to apply: a rotating ladder legitimately
      // accepts 96 x 12 against a 48 wide stock, and the containment search
      // already refused anything no panel covers.
      if (!this.panelsMode &&
          (this.currentWidth > this.effMaxWidth() || this.currentLength > this.effMaxLength())) {
        window.location.href = this.buildRfqUrl(this.currentWidth, this.currentLength);
        return;
      }

      this.addToCartBtn.disabled = true;
      this.addToCartBtn.textContent = DPS_T.adding || 'Adding...';

      // Build sections list for cart drawer refresh (matches theme pattern from custom-cut.js)
      var cartDrawer = document.querySelector('cart-drawer');
      var sections = 'cart-icon-bubble';
      if (cartDrawer) {
        var cartSection = cartDrawer.closest('.shopify-section');
        if (cartSection) {
          sections += ',' + cartSection.id.replace('shopify-section-', '');
        }
      }

      var body = {
        items: [{
          id: price.variantId,
          quantity: this.currentQty,
          properties: {
            'Your Cut': this.currentWidth + '" \u00d7 ' + this.currentLength + '"',
            '_Price Bracket': price.roundedW + '" \u00d7 ' + price.roundedL + '"',
          }
        }],
        sections_url: window.location.pathname,
        sections: sections,
      };

      try {
        var res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });

        if (!res.ok) {
          var err = await res.json();
          throw new Error(err.description || DPS_T.couldNotAdd || 'Could not add to cart');
        }

        var data = await res.json();

        // Success
        var addedTmpl = DPS_T.added || 'Added! {n} x {size} \u2014 {total}';
        this.addToCartBtn.textContent = addedTmpl
          .replace('{n}', this.currentQty)
          .replace('{size}', this.inch(this.currentWidth) + ' \u00d7 ' + this.inch(this.currentLength))
          .replace('{total}', this.fmt(price.total * this.currentQty));
        this.addToCartBtn.classList.add('added');

        // Update cart icon count first
        if (typeof ProductForm !== 'undefined' && ProductForm.updateCartIcon) {
          ProductForm.updateCartIcon(data);
        }

        // Open cart drawer or redirect to cart page (respects theme setting)
        var afterAtc = (theme && theme.settings && theme.settings.afterAtc) || 'drawer';
        if (afterAtc === 'page') {
          setTimeout(function() { window.location.href = (theme && theme.routes && theme.routes.cart) || '/cart'; }, 300);
          return;
        }

        if (cartDrawer && data.sections) {
          var openDrawer = afterAtc === 'drawer' || (afterAtc === 'drawer_desktop' && window.matchMedia('(min-width: 768px)').matches);
          cartDrawer.renderContents(data, openDrawer);
        } else if (!cartDrawer) {
          // Fallback: drawer element not found, redirect to cart
          setTimeout(function() { window.location.href = (theme && theme.routes && theme.routes.cart) || '/cart'; }, 300);
          return;
        }

        setTimeout((function() {
          this.addToCartBtn.textContent = DPS_T.addToCart || 'Add to Cart';
          this.addToCartBtn.classList.remove('added');
          this.addToCartBtn.disabled = false;
        }).bind(this), 2500);

      } catch (err) {
        this.addToCartBtn.textContent = err.message || DPS_T.errorTryAgain || 'Error \u2014 try again';
        this.addToCartBtn.disabled = false;
        setTimeout((function() {
          this.addToCartBtn.textContent = DPS_T.addToCart || 'Add to Cart';
        }).bind(this), 3000);
      }
    }
  }

  customElements.define('dynamic-pricing-sheet', DynamicPricingSheet);
}

/* Node-only export so the pure panel functions can be pinned against
   sheet-bracket-pricing/TEST-VECTORS.json without a browser. Guarded, so the
   theme asset stays a plain script in Shopify (no bundler, no module system).
   See scripts/tests/sheet-panel-pdp-harness.test.js. */
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    DPS_PANELS_MODEL: DPS_PANELS_MODEL,
    DPS_ENGINE_VERSION: DPS_ENGINE_VERSION,
    DPS_DEFAULT_MIN_CUT_IN: DPS_DEFAULT_MIN_CUT_IN,
    DPS_MIN_HATCH_PX: DPS_MIN_HATCH_PX,
    DPS_MIN_TIER_DISCOUNT: DPS_MIN_TIER_DISCOUNT,
    DPS_CUT_STATUS: DPS_CUT_STATUS,
    dpsChargeFromQuantityTable: dpsChargeFromQuantityTable,
    DPS_GRID_TOLERANCE_IN: DPS_GRID_TOLERANCE_IN,
    DPS_PANEL_STR: DPS_PANEL_STR,
    dpsClassifyCut: dpsClassifyCut,
    dpsAssignPanel: dpsAssignPanel,
    dpsPanelOf: dpsPanelOf,
    dpsLadderFromMetafieldValue: dpsLadderFromMetafieldValue,
    dpsComparePanels: dpsComparePanels,
    dpsFormatRung: dpsFormatRung,
    dpsPanelKey: dpsPanelKey,
    dpsMakePanel: dpsMakePanel,
    dpsFits: dpsFits,
  };
}
